// Pure helper (unit-tested in test/javascript/pdf_editor_comment_validation_test.js):
// trims a raw comment value; returns "" for null/undefined/whitespace-only input.
function normalizeComment(value) {
  return (value == null ? "" : String(value)).trim();
}

// Pure annotation cache helpers (unit-tested in test/javascript/pdf_annotation_cache_test.js):
let draftPersistTimeout = null;

function cancelPendingDraftPersist() {
  clearTimeout(draftPersistTimeout);
  draftPersistTimeout = null;
}

function assignAnnotationObjectIds(json) {
  if (!json || !Array.isArray(json.objects)) return json;
  json.objects.forEach(function (obj, index) {
    obj.data = obj.data || {};
    obj.data.__erx = obj.data.__erx || {};
    if (!obj.data.__erx.id) {
      obj.data.__erx.id = `ann-${index}-${obj.type}-${obj.left}-${obj.top}`;
    }
  });
  return json;
}

function emptyAnnotationJson(page, rev) {
  return { version: "4.6.0", objects: [], __erx: { rev, page, ts: Date.now() } };
}

// True when localKey holds a usable full-canvas cache (not a legacy additions-only delta).
function shouldUsePageCache(cachedJson, rev) {
  if (!cachedJson || !Array.isArray(cachedJson.objects)) return false;
  if (cachedJson.__erx?.rev && cachedJson.__erx.rev !== rev) return false;
  if (cachedJson.objects.length > 0 &&
      cachedJson.objects.every(function (obj) { return obj.data?.__erx?.draftOnly; })) {
    return false;
  }
  return true;
}

// Pure: pick annotation JSON after a server fetch (unit-tested).
// Prefer dirty draft cache, then server truth, then published baseline.
function resolveAnnotationLoadSource({ serverJson, draftJson, publishedJson, rev, page }) {
  if (draftJson && shouldUsePageCache(draftJson, rev)) {
    return {
      json: draftJson,
      fromPageCache: true,
      seedPublished: false,
      purgeDraft: false,
      purgePublished: false
    };
  }

  if (serverJson) {
    const objects = Array.isArray(serverJson.objects) ? serverJson.objects : [];
    const normalized = {
      version: serverJson.version || "4.6.0",
      objects: objects,
      __erx: Object.assign({}, serverJson.__erx || {}, { rev: rev, page: page })
    };
    const empty = objects.length === 0;
    return {
      json: empty ? emptyAnnotationJson(page, rev) : normalized,
      fromPageCache: false,
      seedPublished: !empty,
      purgeDraft: empty,
      purgePublished: empty
    };
  }

  if (publishedJson) {
    return {
      json: publishedJson,
      fromPageCache: false,
      seedPublished: false,
      purgeDraft: false,
      purgePublished: false
    };
  }

  return {
    json: emptyAnnotationJson(page, rev),
    fromPageCache: false,
    seedPublished: false,
    purgeDraft: true,
    purgePublished: false
  };
}

// Pure: build localStorage key base for a plan annotations path + composite revision.
function annotationsLocalKeyBase(annotationsPath, revision) {
  return `${annotationsPath || "pdf-annotations"}::rev:${revision || ""}`;
}

// Pure: apply a new composite revision from pdf_status (unit-tested).
// Returns true when revision changed so callers can purge other-rev caches.
function applyEditorRevisionChange(state, newRevision, annotationsPath) {
  if (!state || !newRevision || newRevision === state.revision) return false;
  state.revision = newRevision;
  state.localKeyBase = annotationsLocalKeyBase(annotationsPath, newRevision);
  return true;
}

// True while a page render or save/rebuild is in flight (unit-tested).
function isEditorToolbarLocked(state) {
  return !!(state.rendering || state.saving);
}

async function initPdfEditor() {
  const container = document.querySelector(".pdf-editor");
  if (!container) return;
  if (container.dataset.initialized === "true") return;
  container.dataset.initialized = "true";

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  async function waitFor(fn, limitMs = 4000) {
    const start = Date.now();
    while (!fn()) {
      if (Date.now() - start > limitMs) throw new Error("pdf editor libs not loaded");
      await sleep(50);
    }
  }
  await waitFor(() => window.pdfjsLib && window.fabric && window.PDFLib);

  const SERIALIZE_PROPS = ["data", "name", "_originalSize", "_baseWidth", "_baseHeight", "_baseRx", "_baseRy", "_baseStrokeWidth"];

  const editorState = {
    pdfUrl: container.dataset.pdfUrl,
    currentPdfUrlPath: container.dataset.currentPdfUrlPath || null,
    saveUrl: container.dataset.amendmentsUrl, 
    statusUrl: container.dataset.statusUrl || null, 
    replaceUrl: container.dataset.replaceUrl || null, 
    previewUrl: container.dataset.previewUrl || null, 
    annotationsUrl: container.dataset.annotationsUrl || null,
    textEditsUrl: container.dataset.textEditsUrl || null,
    revision: container.dataset.revision || "", 
    userId: container.dataset.userId || null,
    scale: 1.2,
    userZoom: 1.0,
    pdfDoc: null,
    pageNum: 1,
    pageCount: 0,
    rendering: false,
    renderPending: false,
    mode: "select",
    history: [],
    future: [],
    busy: false,
    saving: false,
    renderTask: null,
    _loadingTask: null,
    suppressHistory: false,
    pageTextContent: null,
    hasUnsavedChanges: false,
    lastSavedState: null,
    textEdits: {}, // page -> array of text block edits
    pendingTextEdits: {}, // page -> { blockIndex: editResult } — accumulated in-memory, flushed on Save Changes
    pageTextEditDisabled: false, // true when page is too complex for text editing
    localKeyBase:
    (
      container.dataset.annotationsKey ||
      (container.dataset.annotationsUrl
        ? new URL(container.dataset.annotationsUrl, window.location.origin).pathname
        : "pdf-annotations"
      )
    ) + `::rev:${container.dataset.revision || ""}`,
  };

  const DEBUG_TEXT_BLOCKS = container.dataset.debugTextBlocks === "true";
  let pdfDebug = DEBUG_TEXT_BLOCKS;
  try {
    pdfDebug = DEBUG_TEXT_BLOCKS || (typeof localStorage !== "undefined" && localStorage.getItem("erxPdfDebug") === "1");
  } catch (e) {
    pdfDebug = DEBUG_TEXT_BLOCKS;
  }
  function debugLog() {
    if (pdfDebug) console.log.apply(console, arguments);
  }


  const MAX_HISTORY_SIZE = 50;
  const DRAFT_PERSIST_DEBOUNCE_MS = 300;

  function annotationsStorageBase() {
    return container.dataset.annotationsKey ||
      (editorState.annotationsUrl
        ? new URL(editorState.annotationsUrl, window.location.origin).pathname
        : "pdf-annotations");
  }

  function localKey(page) {
    return `${editorState.localKeyBase}::page:${page}`;
  }

  function publishedBaselineKey(page) {
    return `${annotationsStorageBase()}::published::rev:${editorState.revision || ""}::page:${page}`;
  }

  function readPublishedBaseline(page) {
    try {
      const raw = localStorage.getItem(publishedBaselineKey(page));
      if (!raw) return null;
      const json = JSON.parse(raw);
      if (json.__erx?.rev && json.__erx.rev !== editorState.revision) return null;
      return assignAnnotationObjectIds(json);
    } catch {
      return null;
    }
  }

  function writePublishedBaseline(page, json) {
    try {
      const normalized = assignAnnotationObjectIds(JSON.parse(JSON.stringify(json)));
      normalized.__erx = {
        rev: editorState.revision,
        page,
        savedAt: Date.now(),
      };
      localStorage.setItem(publishedBaselineKey(page), JSON.stringify(normalized));
    } catch (e) {
      if (e.name === "QuotaExceededError" || e.code === 22) {
        console.warn("[pdf_editor] localStorage quota exceeded when writing published baseline");
      }
    }
  }

  function purgePageDraftKeys(page) {
    try {
      localStorage.removeItem(localKey(page));
      localStorage.removeItem(lastKnownKey(page));
    } catch {}
  }

  function seedPublishedBaselineFromServer(page, serverJson) {
    const rev = editorState.revision;
    const baseline = assignAnnotationObjectIds({
      version: serverJson?.version || "4.6.0",
      objects: Array.isArray(serverJson?.objects) ? serverJson.objects : [],
    });
    if (baseline.objects.length > 0) {
      writePublishedBaseline(page, baseline);
      return baseline;
    }
    return emptyAnnotationJson(page, rev);
  }

  function writePageCache(page, canvasJson) {
    try {
      if (!editorState.hasUnsavedChanges) {
        localStorage.removeItem(localKey(page));
        return;
      }
      const payload = {
        version: canvasJson.version || "4.6.0",
        objects: Array.isArray(canvasJson.objects) ? canvasJson.objects : [],
        __erx: { rev: editorState.revision, page, ts: Date.now() },
      };
      localStorage.setItem(localKey(page), JSON.stringify(payload));
    } catch (e) {
      if (e.name === "QuotaExceededError" || e.code === 22) {
        console.warn("[pdf_editor] localStorage quota exceeded when persisting page cache");
      }
    }
  }

  function persistLocalDraft(page) {
    clearTimeout(draftPersistTimeout);
    draftPersistTimeout = setTimeout(function () {
      writePageCache(page, canvas.toDatalessJSON(SERIALIZE_PROPS));
    }, DRAFT_PERSIST_DEBOUNCE_MS);
  }

  function flushLocalDraft(page) {
    cancelPendingDraftPersist();
    writePageCache(page, canvas.toDatalessJSON(SERIALIZE_PROPS));
  }

  function commitPublishedBaselineAfterSave(page) {
    cancelPendingDraftPersist();
    const json = canvas.toDatalessJSON(SERIALIZE_PROPS);
    writePublishedBaseline(page, json);
    purgePageDraftKeys(page);
    editorState.history = [json];
    editorState.future = [];
    editorState.hasUnsavedChanges = false;
    editorState.lastSavedState = JSON.stringify(json);
    updateSaveButtonState();
  }

  function applyAnnotationJsonToCanvas(json, page, opts) {
    opts = opts || {};
    editorState.suppressHistory = true;

    const finishLoad = function (baselineJson) {
      editorState.history = [baselineJson];
      editorState.future = [];
      editorState.hasUnsavedChanges = !!opts.fromPageCache;
      editorState.lastSavedState = JSON.stringify(baselineJson);
      editorState.suppressHistory = false;
      updateSaveButtonState();
      renderTextLayer();
    };

    if (!json.objects || json.objects.length === 0) {
      canvas.clear();
      canvas.renderAll();
      finishLoad({
        version: json.version || "4.6.0",
        objects: [],
        __erx: json.__erx || { rev: editorState.revision, page },
      });
      return;
    }

    canvas.loadFromJSON(json, function () {
      normalizeLoadedObjects();
      canvas.renderAll();
      finishLoad(canvas.toDatalessJSON(SERIALIZE_PROPS));
    });
  }

  // Returns localStorage keys for signature storage, scoped by user ID
  function getSignatureStorageKeys() {
    const userId = editorState.userId || "anonymous";
    return {
      png: `erx_signature_png::user:${userId}`,
      data: `erx_signature_data::user:${userId}`
    };
  }

  // Purge cached JSON for other PDF revisions so page-shifts don't pick up stale data
  function purgeOtherRevLocalCaches() {
    const base = container.dataset.annotationsKey ||
                 (editorState.annotationsUrl
                   ? new URL(editorState.annotationsUrl, window.location.origin).pathname
                   : "pdf-annotations");

    const curRevPart = `::rev:${editorState.revision || ""}::`;
    const revPrefix  = `${base}::rev:`;
    const publishedPrefix = `${base}::published::rev:`;
    const lastPrefix = `${base}::last::page:`;

    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k) continue;
      if ((k.startsWith(revPrefix) && !k.includes(curRevPart)) ||
          (k.startsWith(publishedPrefix) && !k.includes(curRevPart)) ||
          k.startsWith(lastPrefix)) {
        try { localStorage.removeItem(k); } catch {}
      }
    }
  }

  purgeOtherRevLocalCaches();

  function withCacheBuster(url) {
    const querySeparator = url.includes("?") ? "&" : "?";
    return `${url}${querySeparator}_cb=${Date.now()}`;
  }
  // Ensures the PDF document is loaded, can force reload if needed
  async function ensurePdfDoc({ force = false } = {}) {
    if (force) {
      try { await editorState._loadingTask?.destroy?.(); } catch {}
      try { await editorState.pdfDoc?.destroy?.(); } catch {}
      editorState._loadingTask = null;
      editorState.pdfDoc = null;
    }
    if (!editorState.pdfDoc) {
      try {
        const response = await fetch(withCacheBuster(editorState.pdfUrl), {
          method: 'HEAD',
          credentials: 'same-origin',
          headers: { "Cache-Control": "no-cache" },
        });
        if (!response.ok) {
          if (response.status === 404) {
            console.warn("[pdf_editor] PDF blob not found (404), likely deleted after page operation. Reloading page...");
            setTimeout(() => window.location.reload(), 1000);
            throw new Error("PDF blob not found - page will reload");
          }
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
      } catch (error) {
        if (error.message.includes("PDF blob not found")) {
          throw error; // Re-throw to prevent PDF.js from trying to load
        }
        // For other errors, continue and let PDF.js handle it
        console.warn("[pdf_editor] PDF URL check failed, continuing with PDF.js load:", error);
      }

      const loadingTask = window.pdfjsLib.getDocument({
        url: withCacheBuster(editorState.pdfUrl),
        withCredentials: false,
        httpHeaders: { "Cache-Control": "no-cache" },
      });
      editorState._loadingTask = loadingTask;
      
      try {
      editorState.pdfDoc = await loadingTask.promise;
      } catch (error) {
        if (error.message && (error.message.includes("404") || error.message.includes("not found") || error.message.includes("Failed to fetch"))) {
          console.warn("[pdf_editor] PDF.js failed to load PDF (likely 404), reloading page...");
          setTimeout(() => window.location.reload(), 1000);
          throw error;
        }
        throw error;
      }
    }
    return editorState.pdfDoc;
  }

  if (window.pdfjsLib) {
    const defaultWorker =
      document.querySelector('meta[name="pdfjs-worker-src"]')?.content ||
      "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
    window.pdfjsLib.GlobalWorkerOptions.workerSrc =
      window.pdfjsLib.GlobalWorkerOptions.workerSrc || defaultWorker;
  }

  const sizer = document.getElementById("pdf-canvas-sizer") || container;
  const baseCanvas = document.getElementById("pdf-canvas");
  const baseCtx = baseCanvas.getContext("2d");
  const textLayer = document.getElementById("pdf-text-layer");
  const overlayCanvas = document.getElementById("pdf-overlay");
  const editModeOverlay = document.getElementById("pdf-edit-mode-overlay");
  const canvasWrapper = baseCanvas?.parentElement;

  sizer.style.position = "relative";
  baseCanvas.style.display = "block";
  if (textLayer) {
    textLayer.style.position = "absolute";
    textLayer.style.left = "0";
    textLayer.style.top = "0";
    textLayer.style.zIndex = "11";
    textLayer.style.pointerEvents = "none";
    textLayer.style.display = "block";
    textLayer.style.visibility = "hidden";
    textLayer.style.opacity = "1";
  }
  overlayCanvas.style.position = "absolute";
  overlayCanvas.style.left = "0";
  overlayCanvas.style.top = "0";
  overlayCanvas.style.zIndex = "10";

  const btnSelect   = document.getElementById("tool-select");
  const btnPan      = document.getElementById("tool-panzoom");
  const btnRect     = document.getElementById("tool-rect-highlight");
  const btnPen      = document.getElementById("tool-pen-highlight");
  const btnFreeDraw = document.getElementById("tool-free-draw");
  const btnEllipse  = document.getElementById("tool-ellipse");
  const btnEditText = document.getElementById("tool-edit-text");
  const btnCheckbox = document.getElementById("tool-checkbox");
  const btnEraser   = document.getElementById("tool-eraser");
  const btnCancelTextEdits = document.getElementById("cancel-text-edits");
  const textEditingControls = document.getElementById("text-editing-controls");
  
  let textEditor = null;
  function getTextEditor() {
    // Recreate textEditor if textLayer has changed (e.g., after page navigation)
    if (textEditor && textEditor.textLayer !== textLayer) {
      // Cancel any active editing before discarding old instance
      if (textEditor.isEditing()) {
        textEditor.cancelEditing();
      }
      textEditor = null;
    }
    if (!textEditor && textLayer && window.TextEditor) {
      textEditor = new window.TextEditor(textLayer, canvas);
      // Edits are stored in pendingTextEdits until "Save Changes" is clicked
      textEditor.onSave = (editResult) => {
        if (editResult) {
          const page = editorState.pageNum;
          if (!editorState.pendingTextEdits[page]) editorState.pendingTextEdits[page] = {};
          editorState.pendingTextEdits[page][editResult.index] = editResult;
          if (DEBUG_TEXT_BLOCKS) debugLog("[pdf_editor] Pending edit stored for block", editResult.index, "total pending:", Object.keys(editorState.pendingTextEdits[page]).length);
          renderTextLayer();
        }
      };
      textEditor.onPreviewUpdate = (previewData) => {
        // Live preview: re-render text layer with the in-progress edit
        renderTextLayer();
      };
    }
    return textEditor;
  }
  const btnUndo     = document.getElementById("undo");
  const btnRedo     = document.getElementById("redo");
  const btnPrev     = document.getElementById("prev");
  const btnNext     = document.getElementById("next");
  const btnSave     = document.getElementById("save");
  // Single required comment box shared by annotation and text-edit saves.
  // Read fresh at save time so the gate always uses the box currently in the DOM.
  const COMMENT_INPUT_ID = "edit-comment";
  function getCommentInput() { return document.getElementById(COMMENT_INPUT_ID); }
  const pageNumEl   = document.getElementById("page-num");
  const pageCountEl = document.getElementById("page-count");
  const pageNumInput = document.getElementById("page-num-input");
  const btnFullscreen = document.getElementById("full-screen");

  const btnSignature = document.getElementById("tool-signature");
  const sigDialog = document.getElementById("sig-dialog");
  const sigPad = document.getElementById("sig-pad");
  const sigClear = document.getElementById("sig-clear");
  const sigSave = document.getElementById("sig-save");
  const sigCancel = document.getElementById("sig-cancel");
  const sigCtx = sigPad ? sigPad.getContext("2d") : null;
  
  const btnImageUpload = document.getElementById("tool-image-upload");
  const imageUploadInput = document.getElementById("image-upload-input");
  
  const sigTypeInput = document.getElementById("sig-type-input");
  const sigFontSelect = document.getElementById("sig-font-select");
  const sigDescInput = document.getElementById("sig-desc-input");
  const sigDescClear = document.getElementById("sig-desc-clear");
  const sigSaveCheckbox = document.getElementById("sig-save-checkbox");
  const sigTypePreview = document.getElementById("sig-type-preview");
  const sigImageInput = document.getElementById("sig-image-input");
  const sigImagePreview = document.getElementById("sig-image-preview");
  const sigTabType = document.getElementById("sig-tab-type");
  const sigTabDraw = document.getElementById("sig-tab-draw");
  const sigTabImage = document.getElementById("sig-tab-image");
  const sigPaneType = document.getElementById("sig-pane-type");
  const sigPaneDraw = document.getElementById("sig-pane-draw");
  const sigPaneImage = document.getElementById("sig-pane-image");
  const sigSavedSection = document.getElementById("sig-saved-section");
  const sigSavedList = document.getElementById("sig-saved-list");
  const sigClearSaved = document.getElementById("sig-clear-saved");

  const btnTextbox        = document.getElementById("tool-textbox");
  const btnLine           = document.getElementById("tool-line");

  const toolSettingsPanel = document.getElementById("tool-settings-panel");
  const toolColorPicker = document.getElementById("tool-color-picker");
  const toolColorHex = document.getElementById("tool-color-hex");
  const toolOpacitySlider = document.getElementById("tool-opacity-slider");
  const toolOpacityValue = document.getElementById("tool-opacity-value");
  const toolSizeSlider = document.getElementById("tool-size-slider");
  const toolSizeValue = document.getElementById("tool-size-value");
  const toolSettingsMode = document.getElementById("tool-settings-mode");

  const toolSettings = {
    "pen-highlight": { color: "#ffff00", opacity: 0.5, size: 20 },
    "free-draw": { color: "#000000", opacity: 1.0, size: 3 },
    "rect": { color: "#ffff00", opacity: 0.5, size: 30 },
    "ellipse": { color: "#0000ff", opacity: 0.5, size: 30 },
    "line": { color: "#000000", opacity: 1.0, size: 3 },
  };

  const canvas = new fabric.Canvas(overlayCanvas, {
    selection: true,
    isDrawingMode: false,
    preserveObjectStacking: true,
    enableRetinaScaling: false,
  });

  let fabricContainerEl = canvas.getElement().parentNode;
  Object.assign(fabricContainerEl.style, {
    position: "absolute",
    left: "0",
    top: "0",
    zIndex: "10",
    pointerEvents: "auto",
  });

  // Synchronizes the size of base canvas, overlay canvas, and Fabric.js canvas
  // so all four layers stack pixel-perfect at the current PDF.js viewport.
  function syncOverlaySize(widthPx, heightPx) {
    baseCanvas.width = widthPx;
    baseCanvas.height = heightPx;
    overlayCanvas.width = widthPx;
    overlayCanvas.height = heightPx;
    if (textLayer) {
      textLayer.style.width = widthPx + "px";
      textLayer.style.height = heightPx + "px";
    }
    if (editModeOverlay) {
      editModeOverlay.style.width = widthPx + "px";
      editModeOverlay.style.height = heightPx + "px";
    }
    canvas.setWidth(widthPx);
    canvas.setHeight(heightPx);
    canvas.calcOffset();
    if (fabricContainerEl) {
      fabricContainerEl.style.width = widthPx + "px";
      fabricContainerEl.style.height = heightPx + "px";
    }
  }
  
  function renderTextLayer() {
    debugLog("[pdf_editor] renderTextLayer called");
    if (!textLayer) {
      console.warn("[pdf_editor] Text layer element not found");
      return;
    }
    
    if (!editorState.pageTextContent || !editorState.pageTextContent.textItems) {
      console.warn("[pdf_editor] No text content available to render for page " + (editorState.pageNum || "?") + " (Edit Text will show nothing)");
      textLayer.innerHTML = '';
      return;
    }
    
    const textItems = editorState.pageTextContent.textItems;
    debugLog("[pdf_editor] Rendering text layer with", textItems.length, "text items for page", editorState.pageNum);
    
    if (!textItems || textItems.length === 0) {
      console.warn("[pdf_editor] Page " + editorState.pageNum + " has 0 text items; text layer empty (page may be image-based or flattened)");
      textLayer.innerHTML = '';
      return;
    }
    
    textLayer.innerHTML = '';
    
    const textBlocks = groupTextItems(textItems);
    debugLog("[pdf_editor] Grouped into", textBlocks.length, "text blocks");
    
    if (textBlocks.length === 0) {
      console.warn("[pdf_editor] No text blocks to render");
      return;
    }

    const pageEdits = editorState.textEdits[editorState.pageNum] || [];
    const pendingEdits = editorState.pendingTextEdits[editorState.pageNum] || {};
    const inEditMode = editorState.mode === 'edit-text';

    // Pair a PDF.js block with its saved edit. After a rebuild PDF.js appends
    // injected text last, so blockIndex no longer matches edit.index — we
    // match by content first. Order: text+tight-pos, identity string, index
    // hint (only when its text agrees), then text-only.
    const findEditForBlock = (block, blockIndex) => {
      const blockX = block.x || 0;
      const blockY = block.y || 0;
      const blockText = normBlock(block.text || '');
      const blockTextSpaceless = blockText.replace(/[\s\-]/g, '');

      const textMatches = (candidate) => {
        if (!candidate || !blockText) return false;
        const candidateOriginal = normBlock(candidate.originalText || candidate.pdfOriginalText || candidate.text || '');
        const candidateEdited = normBlock(candidate.text || '');
        return candidateOriginal === blockText
          || candidateEdited === blockText
          || candidateOriginal.replace(/[\s\-]/g, '') === blockTextSpaceless
          || candidateEdited.replace(/[\s\-]/g, '') === blockTextSpaceless;
      };
      const positionClose = (candidate, tolerancePx) =>
        Math.abs((candidate.x || 0) - blockX) < tolerancePx &&
        Math.abs((candidate.y || 0) - blockY) < tolerancePx;

      const textPosMatch = pageEdits.find(candidate => textMatches(candidate) && positionClose(candidate, 10));
      if (textPosMatch) return textPosMatch;

      const expectedIdentity = `${blockText}@${Math.round(blockX / 10)},${Math.round(blockY / 10)}`;
      const identityMatch = pageEdits.find(candidate => candidate.identity && candidate.identity === expectedIdentity);
      if (identityMatch) return identityMatch;

      const indexHint = pageEdits.find(candidate => candidate.index === blockIndex);
      if (indexHint && textMatches(indexHint)) return indexHint;

      const textOnlyMatches = pageEdits.filter(candidate => textMatches(candidate));
      if (textOnlyMatches.length === 1) return textOnlyMatches[0];

      // Ambiguous matches are the most common "edit disappeared after rebuild"
      // failure mode; surface under the debug flag instead of silently dropping.
      if (DEBUG_TEXT_BLOCKS && textOnlyMatches.length > 1) {
        console.warn(
          `[pdf_editor] findEditForBlock: ambiguous text-only match for blockIndex=${blockIndex} ` +
          `candidates=${textOnlyMatches.length} text=${blockText.slice(0, 30).replace(/\s+/g, ' ').trim()}`
        );
      }
      return null;
    };

    // An edit is valid if:
    //  - its originalText matches current PDF text (edit not yet baked in), OR
    //  - its edited text matches current PDF text (edit already baked into current_file)
    const normSpaceless = (text) => normBlock(text).replace(/[\s\-]/g, '');
    const validEditForIndex = (blockIndex) => {
      const block = textBlocks[blockIndex];
      const edit = findEditForBlock(block, blockIndex);
      if (!edit || !block) return false;
      const editOriginalNorm = normBlock(edit.originalText || edit.original || edit.pdfOriginalText || '');
      const editTextNorm = normBlock(edit.text || '');
      const editPdfOriginalNorm = normBlock(edit.pdfOriginalText || '');
      const blockTextNorm = normBlock(block.text || '');
      if (!blockTextNorm) return false;
      if (editOriginalNorm && (editOriginalNorm === blockTextNorm || normSpaceless(editOriginalNorm) === normSpaceless(blockTextNorm))) return true;
      if (editTextNorm && (editTextNorm === blockTextNorm || normSpaceless(editTextNorm) === normSpaceless(blockTextNorm))) return true;
      if (editPdfOriginalNorm && editPdfOriginalNorm !== editOriginalNorm && (editPdfOriginalNorm === blockTextNorm || normSpaceless(editPdfOriginalNorm) === normSpaceless(blockTextNorm))) return true;
      return false;
    };

    // 0-tolerance vertical overlap so we don't hide blocks on adjacent lines;
    // 2px horizontal pad catches fragments like ", Inc".
    const overlapToleranceH = 2;
    const blocksOverlap = (blockA, blockB) => {
      const leftA = blockA.x - overlapToleranceH;
      const widthA = (blockA.width || 0) + overlapToleranceH * 2;
      const topA = blockA.y;
      const heightA = blockA.height || 0;
      const leftB = blockB.x - overlapToleranceH;
      const widthB = (blockB.width || 0) + overlapToleranceH * 2;
      const topB = blockB.y;
      const heightB = blockB.height || 0;
      return leftA < leftB + widthB && leftA + widthA > leftB && topA < topB + heightB && topA + heightA > topB;
    };

    // In edit mode: use overlap-based consolidation only when we have at least one valid edit.
    // That way we hide original PDF text where an edited version is shown. When there are no
    // valid edits (all stale or none), show all blocks so the user sees consistent content.
    const anyValidEdit = inEditMode && textBlocks.some((_, i) => validEditForIndex(i));
    const blocksToShow = inEditMode && anyValidEdit
      ? textBlocks.filter((block, index) => {
          const hasEdit = validEditForIndex(index);
          const overriddenBy = textBlocks.some((other, otherIndex) => {
            if (otherIndex === index) return false;
            if (!blocksOverlap(block, other)) return false;
            const otherHasEdit = validEditForIndex(otherIndex);
            if (otherHasEdit && !hasEdit) return true;
            if (otherHasEdit === hasEdit && otherIndex > index) return true;
            return false;
          });
          return !overriddenBy;
        })
      : textBlocks;

    if (DEBUG_TEXT_BLOCKS) {
      debugLog("[pdf_editor][DEBUG_TEXT_BLOCKS] Render blocks:", {
        mode: editorState.mode,
        page: editorState.pageNum,
        textBlocksCount: textBlocks.length,
        blocksToShowCount: blocksToShow.length,
        renderedBlockIndices: blocksToShow.map(b => textBlocks.indexOf(b)),
        pageEditsIndices: (editorState.textEdits[editorState.pageNum] || []).map(e => e.index)
      });
    }
    // Aggregate stale-edit count so a single rebuild can't spam N warnings.
    let staleEditCount = 0;
    blocksToShow.forEach((block) => {
      const index = textBlocks.indexOf(block);
      const edit = findEditForBlock(block, index);
      const pending = pendingEdits[index] || null;
      const editOriginal = edit ? normBlock(edit.originalText || edit.original || edit.pdfOriginalText || '') : '';
      const editPdfOriginal = edit ? normBlock(edit.pdfOriginalText || '') : '';
      const editedText = edit ? normBlock(edit.text || '') : '';
      const blockText = normBlock(block.text || '');
      const blockTextSpaceless = blockText.replace(/[\s\-]/g, '');
      const editIsValid = edit && blockText && (
        (editOriginal && (editOriginal === blockText || editOriginal.replace(/[\s\-]/g, '') === blockTextSpaceless)) ||
        (editedText && (editedText === blockText || editedText.replace(/[\s\-]/g, '') === blockTextSpaceless)) ||
        (editPdfOriginal && editPdfOriginal !== editOriginal && (editPdfOriginal === blockText || editPdfOriginal.replace(/[\s\-]/g, '') === blockTextSpaceless))
      );
      if (edit && !editIsValid && blockText) {
        staleEditCount += 1;
        if (DEBUG_TEXT_BLOCKS) {
          console.warn("[pdf_editor][DEBUG_TEXT_BLOCKS] Ignoring stale edit for block " + index + ": stored original/text does not match current PDF. Showing PDF text.");
        }
      }
      if (edit && editIsValid && DEBUG_TEXT_BLOCKS) {
        const hasHtml = edit.html && edit.html !== edit.text &&
                       (edit.html.includes('<span') || edit.html.includes('<font') ||
                        edit.html.includes('<b>') || edit.html.includes('<i>') ||
                        edit.html.includes('<u>') || edit.html.includes('style='));
        debugLog(`[pdf_editor][DEBUG_TEXT_BLOCKS] Applying edit to block ${index}:`, {
          original: block.text,
          edited: edit.text,
          html: edit.html?.substring(0, 100),
          hasHtmlFormatting: hasHtml,
          underline: edit.underline,
          bold: edit.bold,
          italic: edit.italic,
          blockIndex: index,
          editIndex: edit.index
        });
      }
      // Pending edits take priority over saved edits
      const activeEdit = pending || ((edit && editIsValid) ? edit : null);
      const displayBlock = activeEdit || block;
      const hasPendingEdit = !!pending;

      const textDiv = document.createElement('div');
      textDiv.className = 'pdf-text-item';

      const hasHtmlString = !!(activeEdit && activeEdit.html && activeEdit.html !== activeEdit.text &&
                                 (activeEdit.html.includes('<span') || activeEdit.html.includes('<font') ||
                                  activeEdit.html.includes('<b>') || activeEdit.html.includes('<i>') ||
                                  activeEdit.html.includes('<u>') || activeEdit.html.includes('style=')));

      // Server-merged blocks carry `runs`, not html. Non-uniform runs need
      // HTML rendering so partial formatting shows in the textLayer.
      const editRunsArr = activeEdit && Array.isArray(activeEdit.runs) ? activeEdit.runs : null;
      const runsAreNonUniform = !!(editRunsArr && editRunsArr.length && (
        editRunsArr.length > 1 ||
        !!editRunsArr[0].bold      !== !!activeEdit.bold ||
        !!editRunsArr[0].italic    !== !!activeEdit.italic ||
        !!editRunsArr[0].underline !== !!activeEdit.underline
      ));
      const hasHtmlFormatting = hasHtmlString || runsAreNonUniform;

      let baseFontSize = displayBlock.fontSize || block.fontSize || 12;
      const baseFontFamily = displayBlock.fontFamily || block.fontFamily || 'Helvetica';
      const originalFontSize = block.fontSize || 12;
      const userChangedFontSize = activeEdit && activeEdit.fontSize && activeEdit.fontSize !== originalFontSize;

      const effectiveBlockWidth = activeEdit ? (displayBlock.width || block.width || 200) : (block.width || displayBlock.width || 200);
      let effectiveBlockHeight = activeEdit ? (displayBlock.height || block.height || 16) : (block.height || displayBlock.height || 16);

      if (userChangedFontSize) {
        const scale = baseFontSize / originalFontSize;
        effectiveBlockHeight = Math.max(effectiveBlockHeight * scale, baseFontSize * 1.4);
      }

      let overflowCorrection = null;
      let displayText = displayBlock.text || block.text || '';
      if (activeEdit && !hasHtmlFormatting && !userChangedFontSize && !hasPendingEdit) {
        overflowCorrection = measureAndCorrectOverflow(
          displayText,
          effectiveBlockWidth,
          effectiveBlockHeight,
          baseFontSize,
          baseFontFamily,
          displayBlock.bold || block.bold || false,
          displayBlock.italic || block.italic || false
        );
        if (overflowCorrection) {
          if (overflowCorrection.fontSize) baseFontSize = overflowCorrection.fontSize;
          if (overflowCorrection.truncatedText) displayText = overflowCorrection.truncatedText;
        }
      }

      const hasCustomBg = activeEdit && displayBlock.bgColor && displayBlock.bgColor !== '#ffffff' && displayBlock.bgColor !== 'transparent' && !hasHtmlFormatting;
      const blockBg = hasCustomBg ? displayBlock.bgColor : (inEditMode ? 'white' : 'transparent');
      if (hasCustomBg) textDiv.dataset.customBg = displayBlock.bgColor;

      const textColor = activeEdit && !hasHtmlFormatting
        ? (displayBlock.textColor || 'rgba(0, 0, 0, 0.9)')
        : (inEditMode ? 'rgb(33, 37, 41)' : 'rgba(0, 0, 0, 0.3)');

      // Use the PDF block's geometry directly; deriving from viewport pixels
      // compounds rounding error and lets edits drift from their original spot.
      const exactLeft = block.x;
      const exactTop = block.y;
      const exactWidth = Math.max(effectiveBlockWidth, 50);

      textDiv.style.cssText = `
        position: absolute;
        left: ${exactLeft}px;
        top: ${exactTop}px;
        width: ${exactWidth}px;
        height: ${effectiveBlockHeight}px;
        font-size: ${baseFontSize}px;
        font-family: ${baseFontFamily}, sans-serif;
        color: ${textColor};
        background-color: ${blockBg};
        cursor: text;
        user-select: none;
        pointer-events: auto;
        line-height: ${effectiveBlockHeight}px;
        overflow: ${inEditMode ? 'visible' : 'hidden'};
        white-space: ${hasPendingEdit ? 'nowrap' : (hasHtmlFormatting || (overflowCorrection && overflowCorrection.wrap) ? 'normal' : 'pre')};
        ${overflowCorrection && overflowCorrection.wrap ? `max-width: ${exactWidth}px; word-wrap: break-word; overflow-wrap: break-word;` : ''}
        z-index: 100;
        transition: background-color 0.15s ease, color 0.15s ease, border 0.15s ease;
        text-align: ${displayBlock.alignment || 'left'};
        ${!hasHtmlFormatting && (displayBlock.bold || block.bold) ? 'font-weight: bold;' : ''}
        ${!hasHtmlFormatting && (displayBlock.italic || block.italic) ? 'font-style: italic;' : ''}
        ${!hasHtmlFormatting && displayBlock.underline ? 'text-decoration: underline;' : ''}
        ${hasPendingEdit ? 'border: 1px dashed #007bff; background-color: #fffde7 !important;' : ''}
      `;
      if (hasPendingEdit) textDiv.classList.add('pdf-text-item--edited');

      if (inEditMode && !hasHtmlFormatting) {
        try {
          const measureCtx = document.createElement('canvas').getContext('2d');
          const fontWeight = (displayBlock.bold || block.bold) ? 'bold ' : '';
          const fontStyleStr = (displayBlock.italic || block.italic) ? 'italic ' : '';
          measureCtx.font = `${fontStyleStr}${fontWeight}${baseFontSize}px ${baseFontFamily}, sans-serif`;
          const measuredWidth = measureCtx.measureText(displayText).width;
          if (measuredWidth > exactWidth) {
            textDiv.style.width = `${Math.ceil(measuredWidth + baseFontSize * 0.5)}px`;
          }
        } catch (e) {
          console.warn('[pdf_editor] width measurement failed for edit-mode block; keeping bbox width', e);
        }
      }

      if (hasHtmlFormatting) {
        // Pending client-side `html` (richer — color/span/font tags) wins;
        // otherwise derive HTML from server-merged `runs`.
        if (hasHtmlString) {
          textDiv.innerHTML = activeEdit.html;
        } else {
          textDiv.innerHTML = renderRunsToHtml(editRunsArr);
        }

        // Unformatted text nodes need explicit styling to prevent inheritance from container
        const walker = document.createTreeWalker(
          textDiv,
          NodeFilter.SHOW_TEXT,
          null,
          false
        );

        let textNode;
        while (textNode = walker.nextNode()) {
          const parent = textNode.parentElement;
          // <font> tags use a 'color' attribute, not inline style, so we check both
          let hasColor = false;
          let current = parent;
          while (current && current !== textDiv) {
            const computedColor = window.getComputedStyle(current).color;
            if (computedColor && computedColor !== 'rgba(0, 0, 0, 0.3)' &&
                computedColor !== 'rgb(0, 0, 0)' && computedColor !== 'rgba(0, 0, 0, 0)') {
              hasColor = true;
              break;
            }
            if (current.style && current.style.color &&
                current.style.color !== 'rgba(0, 0, 0, 0.3)' &&
                current.style.color !== 'initial') {
              hasColor = true;
              break;
            }
            if (current.tagName === 'FONT' && current.getAttribute('color')) {
              hasColor = true;
              break;
            }
            current = current.parentElement;
          }

          // Wrap unformatted text to prevent container color from affecting it
          if (parent && !parent.hasAttribute('style') && !hasColor &&
              parent.tagName !== 'SPAN' && parent.tagName !== 'B' && parent.tagName !== 'I' && parent.tagName !== 'U') {
            const span = document.createElement('span');
            span.style.fontSize = `${baseFontSize}px`;
            span.style.fontFamily = `${baseFontFamily}, sans-serif`;
            span.style.color = 'rgba(0, 0, 0, 0.9)';
            textNode.parentNode.insertBefore(span, textNode);
            span.appendChild(textNode);
          } else if (parent && !hasColor && parent === textDiv) {
            const span = document.createElement('span');
            span.style.fontSize = `${baseFontSize}px`;
            span.style.fontFamily = `${baseFontFamily}, sans-serif`;
            span.style.color = 'rgba(0, 0, 0, 0.9)';
            textNode.parentNode.insertBefore(span, textNode);
            span.appendChild(textNode);
          }
        }
      } else {
        // Plain-text path (no per-run/HTML formatting). Uniform formatting still
        // arrives via container CSS on textDiv; this branch only sets textContent
        // so untrusted text never reaches innerHTML.
        if (edit && edit.edited && edit.text === '') {
          textDiv.textContent = '';
          textDiv.style.opacity = '0.3';
          textDiv.style.fontStyle = 'italic';
          textDiv.title = 'Click to edit text (currently empty - deleted)';
        } else {
          textDiv.textContent = displayText;
        }
      }
      textDiv.dataset.textIndex = index;
      textDiv.dataset.originalText = block.text;
      textDiv.title = 'Click to edit text';
      
      textDiv.addEventListener('click', function(e) {
        if (editorState.mode === "edit-text") {
          const editor = getTextEditor();
          if (editor && displayBlock) {
            e.stopPropagation();
            // Warn if a block matching a stored originalText opens edit mode
            // with no edit paired — silent re-creation would lose server state.
            const looksLikelyEdited = !activeEdit && pageEdits.some(c => {
              const orig = normBlock(c.originalText || c.pdfOriginalText || c.text || '');
              return orig && orig === normBlock(block.text || '');
            });
            if (looksLikelyEdited) {
              console.warn('[pdf_editor] Click on block whose saved edit could not be matched; entering edit with PDF.js-extracted state only', {
                text: (block.text || '').substring(0, 50),
                blockX: block.x, blockY: block.y,
                pdfjsIndex: index
              });
            }
            // Snapshot the entry-time formatting; saveTextEdits diffs against
            // these to tell what the user actually changed (vs. echoing
            // PDF.js-extracted defaults to the server).
            const originalFontSize = block.fontSize || displayBlock.fontSize || 12;
            const originalFontFamily = block.fontFamily || displayBlock.fontFamily || 'Helvetica';
            const originalBold = !!(block.bold || displayBlock.bold);
            const originalItalic = !!(block.italic || displayBlock.italic);
            const originalUnderline = !!(block.underline || displayBlock.underline);
            const originalTextColor = displayBlock.textColor || block.textColor || '#000000';
            const originalBgColor   = displayBlock.bgColor   || block.bgColor   || null;
            const originalAlignment = displayBlock.alignment || block.alignment || 'left';
            const blockData = {
              text: displayBlock.text || block.text || '',
              html: displayBlock.html || null,
              x: displayBlock.x || block.x || 0,
              y: displayBlock.y || block.y || 0,
              width: displayBlock.width || block.width || 0,
              height: displayBlock.height || block.height || 16,
              fontSize: displayBlock.fontSize || block.fontSize || 12,
              _originalFontSize: originalFontSize,
              fontFamily: displayBlock.fontFamily || block.fontFamily || 'Helvetica',
              _originalFontFamily: originalFontFamily,
              bold: displayBlock.bold || block.bold || false,
              _originalBold: originalBold,
              italic: displayBlock.italic || block.italic || false,
              _originalItalic: originalItalic,
              underline: displayBlock.underline || false,
              _originalUnderline: originalUnderline,
              // Saved per-run B/I/U; renders to HTML in the contentEditable
              // so partial formatting shows on entry.
              runs: Array.isArray(displayBlock.runs) ? displayBlock.runs : null,
              textColor: originalTextColor,
              _originalTextColor: originalTextColor,
              bgColor: originalBgColor,
              _originalBgColor: originalBgColor,
              alignment: originalAlignment,
              _originalAlignment: originalAlignment,
              index: index,
              firstItemIndex: block.firstItemIndex ?? displayBlock.firstItemIndex,
              fontName: block.fontName ?? displayBlock.fontName,
              transform: block.transform ?? displayBlock.transform,
              originalText: displayBlock.originalText || displayBlock.pdfOriginalText || block.text || '',
              pdfOriginalText: displayBlock.pdfOriginalText || displayBlock.originalText || block.text || '',
            };
            // Pass siblings + viewport so the editor can clamp font size against neighbor blocks and page edges.
            const viewport = editorState.pageTextContent && editorState.pageTextContent.viewport;
            const allBlocks = textBlocks;
            editor.startEditing(blockData, this, { allBlocks, viewport });
          }
        }
      });
      
      textDiv.addEventListener('mouseenter', function() {
        if (editorState.mode === "edit-text") {
          this.style.backgroundColor = 'rgba(255, 255, 0, 0.5)';
          this.style.color = 'rgba(0, 0, 0, 0.9)';
          this.style.border = '1px solid rgba(255, 200, 0, 0.7)';
          this.style.borderRadius = '2px';
          this.style.boxShadow = '0 0 4px rgba(255, 200, 0, 0.4)';
        }
      });
      textDiv.addEventListener('mouseleave', function() {
        if (editorState.mode === "edit-text") {
          this.style.backgroundColor = (this.dataset.customBg || 'white');
          this.style.color = 'rgb(33, 37, 41)';
          this.style.border = 'none';
          this.style.boxShadow = 'none';
        }
      });
      
      textLayer.appendChild(textDiv);
      
      if (index < 3) {
        debugLog("[pdf_editor] Text block", index, ":", {
          text: block.text.substring(0, 20),
          x: block.x,
          y: block.y,
          width: block.width,
          height: block.height,
          fontSize: block.fontSize
        });
      }
    });

    if (staleEditCount > 0) {
      console.warn(`[pdf_editor] ${staleEditCount} saved edit(s) on page ${editorState.pageNum} no longer match the current PDF and were skipped`);
    }

    debugLog("[pdf_editor] Text layer now contains", textLayer.children.length, "elements");
    const firstChild = textLayer.firstElementChild;
    if (firstChild) {
      const childStyle = window.getComputedStyle(firstChild);
      debugLog("[pdf_editor] First text item computed style:", {
        display: childStyle.display,
        visibility: childStyle.visibility,
        opacity: childStyle.opacity,
        color: childStyle.color,
        position: childStyle.position,
        left: childStyle.left,
        top: childStyle.top,
        zIndex: childStyle.zIndex
      });
    }
  }
  
  function normBlock(s) {
    return (s == null ? '' : String(s)).trim().replace(/\s+/g, ' ');
  }

  // Renders runs to HTML for the textLayer (mirror of TextEditor._runsToHtml
  // so the textLayer matches the contentEditable on entry).
  function renderRunsToHtml(runs) {
    const escape = (unsafe) => String(unsafe == null ? '' : unsafe)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return runs.map(run => {
      let html = escape(run.text || '');
      if (!html) return html;
      if (run.underline) html = `<u>${html}</u>`;
      if (run.italic)    html = `<i>${html}</i>`;
      if (run.bold)      html = `<b>${html}</b>`;
      return html;
    }).join('');
  }

  // Stable block identity matching backend PlanTextEdit.block_identity.
  // Uses original text + quantized position so edits survive index shifts after rebuild.
  function blockIdentity(block) {
    const text = (block.originalText || block.pdfOriginalText || block.text || '').replace(/\s+/g, ' ').trim();
    const x = Math.round((block.x || 0) / 10);
    const y = Math.round((block.y || 0) / 10);
    return `${text}@${x},${y}`;
  }

  function groupTextItems(textItems) {
    if (!textItems || textItems.length === 0) return [];
    
    const blocks = [];
    const processed = new Set();
    
    textItems.forEach((item, index) => {
      if (processed.has(index)) return;
      
      const block = {
        text: item.text,
        x: item.x,
        y: item.y,
        width: item.width,
        height: item.height,
        fontSize: item.fontSize,
        fontFamily: item.fontFamily,
        bold: item.bold,
        italic: item.italic,
        items: [item],
        firstItemIndex: item.itemIndex,
        fontName: item.fontName,
        transform: item.transform,
      };
      
      const lineY = item.y;
      const lineHeight = item.height;
      const fontSize = item.fontSize || 12;
      const tolerance = Math.max(lineHeight * 1.0, 6);
      const maxSpacing = fontSize * 1.5;

      const sameLineItems = textItems
        .map((otherItem, otherIndex) => ({ otherItem, otherIndex }))
        .filter(({ otherItem, otherIndex }) =>
          otherIndex !== index && !processed.has(otherIndex) &&
          Math.abs(otherItem.y - lineY) < tolerance
        )
        .sort((a, b) => a.otherItem.x - b.otherItem.x);

      let blockRight = item.x + item.width;
      const sameWordGap = Math.min(2, fontSize * 0.15);
      const overlapTolerance = Math.max(10, fontSize * 0.8);
      for (const { otherItem, otherIndex } of sameLineItems) {
        const spacing = otherItem.x - blockRight;
        const len = (otherItem.text || '').length;
        const allowGap = len <= 3 ? Math.max(maxSpacing, fontSize * 6) : maxSpacing;
        if (spacing > -overlapTolerance && spacing < allowGap) {
          block.text += (spacing <= sameWordGap && spacing >= 0 ? '' : (spacing < 0 ? '' : ' ')) + otherItem.text;
          block.width = Math.max(block.width, otherItem.x + otherItem.width - block.x);
          block.items.push(otherItem);
          processed.add(otherIndex);
          blockRight = Math.max(blockRight, otherItem.x + otherItem.width);
        }
      }
      
      blocks.push(block);
      processed.add(index);
    });
    
    return blocks;
  }

  // Phase 1 & 2: Run clustering and structured layout detection.
  // Groups text items into vertical clusters (rows) then checks for column alignment patterns.
  function detectStructuredLayout(textItems) {
    if (!textItems || textItems.length < 6) return { hasColumns: false, hasTable: false, reason: null };

    const DEBUG = container.dataset.debugTextBlocks === "true" || (typeof PDF_EDIT_DEBUG !== 'undefined' && PDF_EDIT_DEBUG);

    // Step 1: Cluster items by similar Y coordinate (rows).
    const yTolerance = 4; // px — items within this vertical distance are same row
    const rows = [];
    const sorted = [...textItems].sort((a, b) => a.y - b.y);

    sorted.forEach(item => {
      const lastRow = rows[rows.length - 1];
      if (lastRow && Math.abs(item.y - lastRow.y) < yTolerance) {
        lastRow.items.push(item);
        lastRow.maxX = Math.max(lastRow.maxX, item.x + (item.width || 0));
      } else {
        rows.push({
          y: item.y,
          items: [item],
          maxX: item.x + (item.width || 0)
        });
      }
    });

    if (DEBUG) debugLog(`[pdf_editor][layout] Detected ${rows.length} text rows from ${textItems.length} items`);

    // Step 2: Extract X positions (left edges) of items in each row.
    const rowXPositions = rows.filter(row => row.items.length >= 2).map(row =>
      row.items.map(item => Math.round(item.x / 5) * 5) // quantize to 5px buckets
    );

    if (rowXPositions.length < 3) return { hasColumns: false, hasTable: false, reason: null };

    // Step 3: Detect consistent X alignment across rows (column pattern).
    // Count how often each quantized X position appears across rows.
    const xFrequency = {};
    rowXPositions.forEach(xPositions => {
      const unique = [...new Set(xPositions)];
      unique.forEach(x => { xFrequency[x] = (xFrequency[x] || 0) + 1; });
    });

    // A column is an X position that appears in at least 40% of multi-item rows
    const minRowCount = Math.max(3, Math.floor(rowXPositions.length * 0.4));
    const columnXPositions = Object.entries(xFrequency)
      .filter(([, count]) => count >= minRowCount)
      .map(([x]) => parseInt(x, 10))
      .sort((a, b) => a - b);

    // Columns must be separated by at least 30px to be distinct
    const distinctColumns = [columnXPositions[0]];
    for (let i = 1; i < columnXPositions.length; i++) {
      if (columnXPositions[i] - distinctColumns[distinctColumns.length - 1] >= 30) {
        distinctColumns.push(columnXPositions[i]);
      }
    }

    const hasColumns = distinctColumns.length >= 3;

    // Step 4: Detect table-like structure (consistent row count with aligned items).
    const rowLengths = rows.filter(row => row.items.length >= 2).map(row => row.items.length);
    const lengthFreq = {};
    rowLengths.forEach(len => { lengthFreq[len] = (lengthFreq[len] || 0) + 1; });
    const mostCommonLength = Object.entries(lengthFreq).sort((a, b) => b[1] - a[1])[0];
    const hasTable = mostCommonLength && parseInt(mostCommonLength[0], 10) >= 3 && mostCommonLength[1] >= 4;

    // Step 5: Block density check — many tightly packed blocks suggest form/table
    const pageArea = textItems.length > 0
      ? (Math.max(...textItems.map(item => item.x + (item.width || 0))) - Math.min(...textItems.map(item => item.x))) *
        (Math.max(...textItems.map(item => item.y + (item.height || 0))) - Math.min(...textItems.map(item => item.y)))
      : 1;
    const blockDensity = textItems.length / Math.max(pageArea, 1) * 10000;
    const isDense = blockDensity > 0.5 && textItems.length > 80;

    let reason = null;
    if (hasColumns) reason = `Detected ${distinctColumns.length} aligned columns across ${minRowCount}+ rows`;
    else if (hasTable) reason = `Table pattern detected: ${mostCommonLength[1]} rows with ${mostCommonLength[0]} columns each`;
    else if (isDense) reason = `High block density: ${textItems.length} items in area (density=${blockDensity.toFixed(2)})`;

    if (DEBUG && reason) debugLog(`[pdf_editor][layout] ${reason}`);

    return { hasColumns, hasTable, isDense, reason, distinctColumns: hasColumns ? distinctColumns : [] };
  }

  // Returns null when text fits, else a strategy hint:
  // { fontSize, strategy: 'shrink' } | { fontSize, wrap: true, lineCount,
  // strategy: 'wrap' } | { truncatedText, strategy: 'truncate' }.
  function measureAndCorrectOverflow(text, blockWidth, blockHeight, fontSize, fontFamily, bold, italic) {
    if (!text || blockWidth <= 0) return null;

    const measureCanvas = document.createElement('canvas');
    const ctx = measureCanvas.getContext('2d');
    const fontStyle = `${italic ? 'italic ' : ''}${bold ? 'bold ' : ''}${fontSize}px ${fontFamily}, sans-serif`;
    ctx.font = fontStyle;
    const measured = ctx.measureText(text);
    const textWidth = measured.width;

    if (textWidth <= blockWidth) return null;

    const overflowRatio = textWidth / blockWidth;
    const DEBUG = container.dataset.debugTextBlocks === "true";
    if (DEBUG) debugLog(`[pdf_editor][overflow] text="${text.substring(0, 30)}..." width=${textWidth.toFixed(1)} block=${blockWidth.toFixed(1)} ratio=${overflowRatio.toFixed(2)}`);

    // Strategy A: Shrink font slightly (up to 30% reduction)
    if (overflowRatio <= 1.4) {
      const shrunkSize = Math.max(Math.floor(fontSize / overflowRatio), Math.ceil(fontSize * 0.7));
      if (DEBUG) debugLog(`[pdf_editor][overflow] Strategy A: shrink font ${fontSize} -> ${shrunkSize}`);
      return { fontSize: shrunkSize, strategy: 'shrink' };
    }

    // Strategy B: Wrap text to next line (allow multi-line)
    if (overflowRatio <= 2.5) {
      const lineCount = Math.ceil(overflowRatio);
      const shrunkSize = Math.max(Math.floor(fontSize * 0.85), 6);
      if (DEBUG) debugLog(`[pdf_editor][overflow] Strategy B: wrap to ${lineCount} lines, font ${fontSize} -> ${shrunkSize}`);
      return { fontSize: shrunkSize, wrap: true, lineCount, strategy: 'wrap' };
    }

    // Strategy C: Truncate with ellipsis
    let truncated = text;
    let truncWidth = textWidth;
    while (truncated.length > 3 && truncWidth > blockWidth) {
      truncated = truncated.slice(0, -4) + '...';
      truncWidth = ctx.measureText(truncated).width;
    }
    if (DEBUG) debugLog(`[pdf_editor][overflow] Strategy C: truncate to "${truncated.substring(0, 30)}"`);
    return { truncatedText: truncated, strategy: 'truncate' };
  }

  function extractTextItems(textContent, viewport) {
    if (!textContent || !textContent.items) return [];
    
    const scale = viewport.scale || 1;
    
    return textContent.items.map((item, itemIndex) => {
      const transform = item.transform || [1, 0, 0, 1, 0, 0];
      const pdfX = transform[4] || 0;
      const pdfY = transform[5] || 0;
      
      const pdfWidth = item.width || 0;
      const pdfHeight = item.height || 0;
      const width = pdfWidth * scale;
      const height = pdfHeight * scale;
      
      const x = pdfX * scale;
      const y = viewport.height - (pdfY * scale) - height;
      
      return {
        text: item.str || '',
        x: x,
        y: y,
        width: width,
        height: height,
        fontSize: height || 12,
        fontName: item.fontName || 'Helvetica',
        fontFamily: extractFontFamily(item.fontName),
        direction: item.dir || 'ltr',
        transform: transform,
        bold: isBoldFont(item.fontName),
        italic: isItalicFont(item.fontName),
        itemIndex: itemIndex, // Stable anchor for server-side matching
      };
    }).filter(item => item.text.trim().length > 0);
  }
  
  function extractFontFamily(fontName) {
    if (!fontName) return 'Helvetica';
    
    const cleanName = fontName.replace(/^[A-Z]{6}\+/, ''); // Remove embedded font prefix
    const baseName = cleanName.split(/[-_]/)[0];
    
    const fontMap = {
      'Helvetica': 'Helvetica',
      'Times': 'Times',
      'Courier': 'Courier',
      'Arial': 'Arial',
      'Calibri': 'Helvetica',
      'Times-Roman': 'Times',
      'Times-Bold': 'Times',
      'Times-Italic': 'Times',
      'Helvetica-Bold': 'Helvetica',
      'Helvetica-Oblique': 'Helvetica',
    };
    return fontMap[baseName] || fontMap[cleanName] || fontMap[fontName] || 'Helvetica';
  }
  
  function isBoldFont(fontName) {
    if (!fontName) return false;
    return /Bold|Black|Heavy|Semibold|Demibold/i.test(fontName);
  }
  
  function isItalicFont(fontName) {
    if (!fontName) return false;
    return /Italic|Oblique/i.test(fontName);
  }

  // Maps the toolbar's 1-100 abstract scale to canvas-relative pixels so the
  // same slider value looks similar across zoom levels.
  function sizeToPixels(sizeValue) {
    const minPixels = 10;
    const maxPixels = Math.min(canvas.width, canvas.height) * 0.5;
    const range = maxPixels - minPixels;
    if (range <= 0) return minPixels + (sizeValue - 1) * 2;
    return minPixels + (sizeValue - 1) * (range / 99);
  }

  function pixelsToSize(pixelValue) {
    const minPixels = 10;
    const maxPixels = Math.min(canvas.width, canvas.height) * 0.5;
    const range = maxPixels - minPixels;
    if (range <= 0) return 50;
    const sizeValue = 1 + ((pixelValue - minPixels) / range) * 99;
    return Math.max(1, Math.min(100, Math.round(sizeValue)));
  }

  function resetTransforms() {
    try { baseCtx.setTransform(1, 0, 0, 1, 0, 0); } catch (_) {}
  }

  // Cancels any in-progress PDF page rendering task
  async function cancelInFlightRender() {
    const task = editorState.renderTask;
    if (!task) return;
    try { await task.cancel(); } catch (_) {}
    editorState.renderTask = null;
  }

  function setBusy(on) {
    editorState.busy = !!on;
    [btnPrev, btnNext, btnSave].forEach((btn) => btn && (btn.disabled = on));
  }

  function setSavingUI(saving) {
    editorState.saving = !!saving;
    syncEditorControls();
  }

  // Snapshot canvas state for undo. Skips no-op snapshots to avoid blowing
  // through MAX_HISTORY_SIZE on idle interactions.
  function snapshot() {
    if (editorState.suppressHistory) return;
    const json = canvas.toDatalessJSON(SERIALIZE_PROPS);
    const last = editorState.history[editorState.history.length - 1];
    const same = last && JSON.stringify(last) === JSON.stringify(json);
    if (!same) {
      editorState.history.push(json);
      if (editorState.history.length > MAX_HISTORY_SIZE) {
        editorState.history.shift();
      }
      editorState.future.length = 0;
      editorState.hasUnsavedChanges = true;
      updateSaveButtonState();
      persistLocalDraft(editorState.pageNum);
    }
  }

  function updateSaveButtonState() {
    if (!btnSave) return;
    if (editorState.hasUnsavedChanges) {
      btnSave.classList.add("btn-warning");
      btnSave.classList.remove("btn-primary");
      btnSave.title = "Save annotation changes";
      btnSave.disabled = false;
    } else {
      btnSave.classList.remove("btn-warning");
      btnSave.classList.add("btn-primary");
      btnSave.title = "No annotation changes to save";
      btnSave.disabled = true;
    }
  }
  // Loads canvas state without pushing onto history (used by undo/redo).
  function loadJSONWithoutSnapshot(json, onLoaded) {
    editorState.suppressHistory = true;
    canvas.loadFromJSON(json, () => {
      normalizeLoadedObjects();
      editorState.suppressHistory = false;
      canvas.renderAll();
      onLoaded && onLoaded();
    });
  }
  function undo() {
    if (editorState.history.length <= 1) return;
    const current = editorState.history.pop();
    editorState.future.push(current);
    const prev = editorState.history[editorState.history.length - 1];
    loadJSONWithoutSnapshot(prev);
  }
  function redo() {
    if (editorState.future.length === 0) return;
    const next = editorState.future.pop();
    editorState.history.push(next);
    loadJSONWithoutSnapshot(next);
  }

  canvas.on("object:added",   (e) => {
    if (!editorState.suppressHistory && !e?.target?._skipHistory) snapshot();
    resetProgressBars();
  });
  canvas.on("object:modified", () => { 
    if (!editorState.suppressHistory) snapshot(); 
    resetProgressBars();
  });
  canvas.on("object:removed",  () => { 
    if (!editorState.suppressHistory) snapshot(); 
    resetProgressBars();
  });

  canvas.on("selection:created", (opt) => {
    updateSettingsPanelForObject(opt.target);
    
    if (editorState.mode === "select" && opt.target && opt.target.type === "group" && opt.target.data?.kind === "checkbox") {
      if (pendingCheckboxToggle && pendingCheckboxToggle === opt.target) {
        clearTimeout(checkboxClickTimeout);
        checkboxClickTimeout = null;
        toggleCheckbox(opt.target);
        pendingCheckboxToggle = null;
      }
    }
  });

  canvas.on("selection:updated", (opt) => {
    updateSettingsPanelForObject(opt.target);
    
    if (editorState.mode === "select" && opt.target && opt.target.type === "group" && opt.target.data?.kind === "checkbox") {
      if (pendingCheckboxToggle && pendingCheckboxToggle === opt.target) {
        clearTimeout(checkboxClickTimeout);
        checkboxClickTimeout = null;
        toggleCheckbox(opt.target);
        pendingCheckboxToggle = null;
      }
    }
  });

  canvas.on("selection:cleared", () => {
    const activeObj = canvas.getActiveObject();
    
    if (activeObj) {
      updateSettingsPanelForObject(activeObj);
      return;
    }
    
    const showSettings = ["pen-highlight", "free-draw", "rect", "ellipse", "line"].includes(editorState.mode);
    showSettingsPanel(showSettings);
    
    if (showSettings) {
      updateToolSettingsUI(editorState.mode);
    }
  });

  // Normalizes objects after loading from JSON and handles legacy checkbox objects
  function normalizeLoadedObjects() {
    canvas.getObjects().forEach((obj) => {
      obj.selectable = true;
      obj.evented = true;

      if (obj.type === "image") {
        obj.set({ stroke: null, strokeWidth: 0 });
      }

      if (obj.type === "group" || obj.type === "activeSelection") {
        (obj._objects || []).forEach((ch) => {
          ch.selectable = false;
          ch.evented = false;
        });
      }

      if (obj.type === "group") {
        if (obj.data?.kind === "checkbox") {
          applyCheckboxTraits(obj);
        } else {
          const rect = (obj._objects || []).find(o => o.type === "rect");
          const poly = (obj._objects || []).find(o => o.type === "polyline");
          if (rect && poly && (obj._objects || []).length === 2) {
            obj.data = { ...(obj.data || {}), kind: "checkbox", checked: !!poly.visible };
            if (!rect.name) rect.name = "box";
            if (!poly.name) poly.name = "check";
            applyCheckboxTraits(obj);
          }
        }
      }
    });
    canvas.requestRenderAll();
  }

  function applyCheckboxTraits(group) {
    group.hasControls = true;
    group.hasBorders = true;
    group.lockScalingFlip = true;
    group.lockRotation = true;
    group.hoverCursor = "pointer";

    const chk = group._objects?.find(o => o.name === "check") || group._objects?.find(o => o.type === "polyline");
    if (chk) {
      chk.visible = !!group.data?.checked;
      chk.selectable = false;
      chk.evented = false;
      if (!chk.name) chk.name = "check";
    }
    const box = group._objects?.find(o => o.name === "box") || group._objects?.find(o => o.type === "rect");
    if (box) {
      box.selectable = false;
      box.evented = false;
      if (!box.name) box.name = "box";
    }
  }

  // Loads annotations for a specific page from server or localStorage cache
  async function loadAnnotations(page) {
    if (!editorState.annotationsUrl) return;

    await loadTextEdits(page);

    const rev = editorState.revision;

    async function fetchJson(withRev) {
      const queryParams = new URLSearchParams({ page: String(page) });
      if (withRev && rev) queryParams.set("rev", rev);
      queryParams.set("_cb", Date.now().toString());
      const url = `${editorState.annotationsUrl}?${queryParams}`;
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) return null;
      const data = await res.json();
      if (!data || typeof data.json === "undefined") return null;
      return typeof data.json === "string" ? JSON.parse(data.json) : data.json;
    }

    let serverJson = null;
    try {
      serverJson = await fetchJson(true);
      if (!serverJson) serverJson = await fetchJson(false);
    } catch (e) {
      console.warn("[pdf_editor] Failed to fetch annotations from server:", e);
    }

    if (serverJson && !Array.isArray(serverJson.objects)) {
      serverJson = { ...serverJson, objects: [] };
    }

    let draftJson = null;
    try {
      const cachedRaw = localStorage.getItem(localKey(page));
      if (cachedRaw) draftJson = JSON.parse(cachedRaw);
    } catch (e) {
      console.warn("[pdf_editor] Failed to read local page cache:", e);
    }

    const publishedJson = readPublishedBaseline(page);
    const decision = resolveAnnotationLoadSource({
      serverJson: serverJson,
      draftJson: draftJson,
      publishedJson: publishedJson,
      rev: rev,
      page: page
    });

    if (decision.purgeDraft) purgePageDraftKeys(page);
    if (decision.purgePublished) {
      try { localStorage.removeItem(publishedBaselineKey(page)); } catch {}
    }
    if (decision.seedPublished && decision.json) {
      writePublishedBaseline(page, decision.json);
    }

    applyAnnotationJsonToCanvas(decision.json, page, { fromPageCache: !!decision.fromPageCache });
  }

  // Resets all progress bars to 0% and clears their state
  function resetProgressBars() {
    const dataProgressBar = document.getElementById("data-upload-progress");
    const previewProgressBar = document.getElementById("preview-upload-progress");
    
    if (dataProgressBar) {
      dataProgressBar.style.width = "0%";
      dataProgressBar.setAttribute("aria-valuenow", "0");
    }
    if (previewProgressBar) {
      previewProgressBar.style.width = "0%";
      previewProgressBar.setAttribute("aria-valuenow", "0");
    }
  }

  let dataProgressResetTimeout = null;
  let previewProgressResetTimeout = null;

  // Saves annotations for a page to the server
  async function saveAnnotations(page, showProgress = false) {
    try {
      const json = canvas.toDatalessJSON(SERIALIZE_PROPS);
      json.__erx = { rev: editorState.revision, page, ts: Date.now() };
    
      const baseline = editorState.history && editorState.history[0]
        ? editorState.history[0]
        : { version: json.version || "4.6.0", objects: [] };
    
      const currentStr  = JSON.stringify(json);
      const baselineStr = JSON.stringify(baseline);
      const dirty = currentStr !== baselineStr;
    
      const isEmpty = !json.objects || json.objects.length === 0;

      if (dirty) {
        flushLocalDraft(page);
      }

      if (!editorState.annotationsUrl) return;
    
      if (!dirty) {
        if (!showProgress) {
          resetProgressBars();
        }
        return;
      }
    
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const progressBar = document.getElementById("data-upload-progress");
      
      if (dataProgressResetTimeout) {
        clearTimeout(dataProgressResetTimeout);
        dataProgressResetTimeout = null;
      }
      
      if (showProgress && progressBar) {
        progressBar.style.width = "0%";
        progressBar.setAttribute("aria-valuenow", "0");
      } else if (!showProgress && progressBar) {
        progressBar.style.width = "0%";
        progressBar.setAttribute("aria-valuenow", "0");
      }
      
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        if (showProgress) {
          xhr.upload.addEventListener("progress", (evt) => {
            if (evt.lengthComputable && progressBar) {
              const percentComplete = Math.floor((evt.loaded / evt.total) * 100);
              progressBar.style.width = percentComplete + "%";
              progressBar.setAttribute("aria-valuenow", percentComplete);
            }
          }, false);
        }
        
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (showProgress && progressBar) {
              progressBar.style.width = "100%";
              progressBar.setAttribute("aria-valuenow", "100");
              dataProgressResetTimeout = setTimeout(() => {
                if (progressBar) {
                  progressBar.style.width = "0%";
                  progressBar.setAttribute("aria-valuenow", "0");
                }
                dataProgressResetTimeout = null;
              }, 2000);
            } else if (progressBar) {
              progressBar.style.width = "0%";
              progressBar.setAttribute("aria-valuenow", "0");
            }
            editorState.hasUnsavedChanges = false;
            editorState.lastSavedState = currentStr;
            updateSaveButtonState();
            
            resolve(xhr.response);
          } else {
            console.warn("Annotations PUT failed", xhr.status);
            if (progressBar) {
              progressBar.style.width = "0%";
              progressBar.setAttribute("aria-valuenow", "0");
            }
            reject(new Error(`Annotations PUT failed: ${xhr.status}`));
          }
        });
        
        xhr.addEventListener("error", () => {
          if (progressBar) {
            progressBar.style.width = "0%";
            progressBar.setAttribute("aria-valuenow", "0");
          }
          reject(new Error("Annotations PUT failed: network error"));
        });
        
        xhr.open("PUT", editorState.annotationsUrl);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");
        if (csrf) {
          xhr.setRequestHeader("X-CSRF-Token", csrf);
        }
        xhr.withCredentials = true;
        const requestBody = { page, json, rev: editorState.revision };
        if (isEmpty) requestBody.wipe = true;
        xhr.send(JSON.stringify(requestBody));
      });
    } catch (e) {
      console.warn("[pdf_editor] saveAnnotations failed", e);
      resetProgressBars();
    }
  }

  // Reload after text edit save so user sees baked-in text (PlanPreviewRebuildJob runs async)
  let reloadAfterTextEditTimeout = null;
  const RELOAD_AFTER_TEXT_EDIT_MS = 3000;

  function scheduleReloadAfterTextEditSave() {
    if (reloadAfterTextEditTimeout) clearTimeout(reloadAfterTextEditTimeout);
    try { sessionStorage.setItem('pdf_editor_restore_page', String(editorState.pageNum)); } catch(_) {}
    if (typeof toastr !== "undefined") {
      toastr.info("Refreshing in a few seconds to show your text changes...", "", { timeOut: 2500 });
    }
    reloadAfterTextEditTimeout = setTimeout(() => {
      reloadAfterTextEditTimeout = null;
      window.location.reload();
    }, RELOAD_AFTER_TEXT_EDIT_MS);
  }

  async function reloadPdfAfterRebuild(returnToPage, options = {}) {
    const showProgress = options.showProgress === true;
    const dataUploadProgressBar = document.getElementById("data-upload-progress");
    const previewRebuildProgressBar = document.getElementById("preview-upload-progress");

    if (!showProgress && typeof toastr !== "undefined") {
      toastr.info("Applying text changes...", "", { timeOut: 6000 });
    }

    if (showProgress && previewRebuildProgressBar) {
      previewRebuildProgressBar.style.width = "0%";
      previewRebuildProgressBar.setAttribute("aria-valuenow", "0");
    }

    const statusUrl = editorState.statusUrl;
    let freshBlobUrl = null;
    const maxAttempts = 25;
    const progressCapPercent = 90;

    if (statusUrl) {
      let attempts = 0;
      while (attempts < maxAttempts) {
        await sleep(attempts < 3 ? 800 : 1500);
        if (showProgress && previewRebuildProgressBar) {
          const percent = Math.min(progressCapPercent, Math.floor((attempts / maxAttempts) * progressCapPercent));
          previewRebuildProgressBar.style.width = percent + "%";
          previewRebuildProgressBar.setAttribute("aria-valuenow", String(percent));
        }
        try {
          const res = await fetch(withCacheBuster(statusUrl), {
            credentials: "same-origin",
            headers: { "Accept": "application/json", "Cache-Control": "no-cache" }
          });
          if (res.ok) {
            const data = await res.json();
            debugLog(`[pdf_editor] pdf_status poll #${attempts + 1}: processing=${data.processing_pdf}`);
            if (applyEditorRevisionChange(editorState, data.revision, annotationsStorageBase())) {
              purgeOtherRevLocalCaches();
            }
            if (!data.processing_pdf) {
              freshBlobUrl = data.blob_url || data.preview_blob_url;
              if (showProgress && previewRebuildProgressBar) {
                previewRebuildProgressBar.style.width = "100%";
                previewRebuildProgressBar.setAttribute("aria-valuenow", "100");
              }
              if (showProgress && typeof toastr !== "undefined") {
                toastr.info("Refreshing page…", "", { timeOut: 2500 });
              }
              break;
            }
          }
        } catch (err) {
          console.warn("[pdf_editor] pdf_status poll failed:", err && err.message, err);
        }
        attempts++;
      }
      if (!freshBlobUrl) {
        console.warn("[pdf_editor] pdf_status polling timed out, trying currentPdfUrlPath");
        if (showProgress && previewRebuildProgressBar) {
          previewRebuildProgressBar.style.width = "100%";
          previewRebuildProgressBar.setAttribute("aria-valuenow", "100");
        }
      }
    } else {
      await sleep(4000);
    }

    if (editorState._loadingTask) {
      try { editorState._loadingTask.destroy(); } catch(_) {}
      editorState._loadingTask = null;
    }
    if (editorState.pdfDoc) {
      try { editorState.pdfDoc.destroy(); } catch(_) {}
      editorState.pdfDoc = null;
    }

    if (freshBlobUrl) {
      editorState.pdfUrl = freshBlobUrl;
    } else if (editorState.currentPdfUrlPath) {
      try {
        const res = await fetch(editorState.currentPdfUrlPath, { credentials: "same-origin" });
        if (res.ok) {
          const data = await res.json();
          if (data.url) editorState.pdfUrl = data.url;
        }
      } catch (_) {}
    }

    editorState.pageNum = returnToPage || editorState.pageNum;
    try {
      await renderPage(editorState.pageNum);
      // Only claim success when the blob actually changed; freshBlobUrl is set when pdf_status returned a new preview blob.
      if (freshBlobUrl && typeof toastr !== "undefined") {
        toastr.success("Text changes applied.", "", { timeOut: 1500 });
      } else if (!freshBlobUrl && typeof toastr !== "undefined") {
        toastr.warning("PDF rebuild is still processing. Refresh in a moment to see your changes.", "", { timeOut: 3000 });
      }
    } catch (err) {
      console.warn("[pdf_editor] reloadPdfAfterRebuild render failed, falling back to page reload:", err);
      try { sessionStorage.setItem('pdf_editor_restore_page', String(returnToPage || editorState.pageNum)); } catch(_) {}
      window.location.reload();
    } finally {
      _textEditSaveInFlight = false;
      setSavingUI(false);
      if (showProgress) {
        const resetBarsAfterMs = 1500;
        const dataUploadProgressLabel = document.getElementById("data-upload-progress-label");
        const previewRebuildProgressLabel = document.getElementById("preview-upload-progress-label");
        setTimeout(() => {
          if (dataUploadProgressBar) {
            dataUploadProgressBar.style.width = "0%";
            dataUploadProgressBar.setAttribute("aria-valuenow", "0");
          }
          if (previewRebuildProgressBar) {
            previewRebuildProgressBar.style.width = "0%";
            previewRebuildProgressBar.setAttribute("aria-valuenow", "0");
          }
          if (dataUploadProgressLabel) dataUploadProgressLabel.textContent = "Data Upload Progress";
          if (previewRebuildProgressLabel) previewRebuildProgressLabel.textContent = "Preview Upload Progress";
        }, resetBarsAfterMs);
      }
    }
  }

  let _textEditSaveInFlight = false;

  async function saveTextEdits(page, showProgress = false, scheduleReloadAfterSave = false, flushedIndices = null, comment = "") {
    if (_textEditSaveInFlight) {
      debugLog(`[pdf_editor] saveTextEdits: already in flight, skipping duplicate for page ${page}`);
      return;
    }
    if (!editorState.textEditsUrl) {
      debugLog(`[pdf_editor] saveTextEdits: no textEditsUrl for page ${page}`);
      setSavingUI(false);
      return;
    }
    _textEditSaveInFlight = true;
    const requestId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    debugLog(`[pdf_editor] saveTextEdits: page=${page} request_id=${requestId}`);

    const dataUploadProgressBar = showProgress ? document.getElementById("data-upload-progress") : null;
    const previewRebuildProgressBar = showProgress ? document.getElementById("preview-upload-progress") : null;
    const dataUploadProgressLabel = showProgress ? document.getElementById("data-upload-progress-label") : null;
    const previewRebuildProgressLabel = showProgress ? document.getElementById("preview-upload-progress-label") : null;

    if (showProgress) {
      if (dataProgressResetTimeout) {
        clearTimeout(dataProgressResetTimeout);
        dataProgressResetTimeout = null;
      }
      if (previewProgressResetTimeout) {
        clearTimeout(previewProgressResetTimeout);
        previewProgressResetTimeout = null;
      }
      if (dataUploadProgressBar) {
        dataUploadProgressBar.style.width = "0%";
        dataUploadProgressBar.setAttribute("aria-valuenow", "0");
      }
      if (previewRebuildProgressBar) {
        previewRebuildProgressBar.style.width = "0%";
        previewRebuildProgressBar.setAttribute("aria-valuenow", "0");
      }
      if (dataUploadProgressLabel) dataUploadProgressLabel.textContent = "Saving text edits…";
      if (previewRebuildProgressLabel) previewRebuildProgressLabel.textContent = "Building preview…";
    }

    const pageEdits = editorState.textEdits[page] || [];
    const textItems = editorState.pageNum === page && editorState.pageTextContent?.textItems;
    const textBlocksFromPdf = textItems && textItems.length > 0 ? groupTextItems(textItems) : [];

    // When flushedIndices is provided, only serialize edits for those block indices
    // (the ones the user actually touched this session). The server merges sparse edits
    // with prior text_block_edits, so re-sending untouched blocks risks overwriting
    // with stale first-save values.
    const edits = {};
    pageEdits.forEach((edit) => {
      const blockIndex = edit.index != null ? Number(edit.index) : pageEdits.indexOf(edit);
      if (flushedIndices instanceof Set && !flushedIndices.has(blockIndex)) return;

      const textChanged =
        (edit.text != null && edit.originalText != null && normBlock(edit.text) !== normBlock(edit.originalText)) ||
        (edit.text != null && edit.pdfOriginalText != null && normBlock(edit.text) !== normBlock(edit.pdfOriginalText));

      // Diff against snapshots captured by the click handler. Only fields with
      // a present snapshot are diffed — server-loaded baseline entries lack
      // snapshots, and synthetic defaults (12pt / Helvetica) would otherwise
      // produce false positives that ship baseline values back to the server.
      const hasSizeSnap      = edit._originalFontSize     != null;
      const hasFamilySnap    = edit._originalFontFamily   != null;
      const hasBoldSnap      = edit._originalBold         != null;
      const hasItalicSnap    = edit._originalItalic       != null;
      const hasUnderSnap     = edit._originalUnderline    != null;
      const hasColorSnap     = edit._originalTextColor    != null;
      const hasBgSnap        = edit._originalBgColor      != null;
      const hasAlignSnap     = edit._originalAlignment    != null;

      const sizeChanged      = hasSizeSnap   && edit.fontSize   != null && Number(edit.fontSize) !== Number(edit._originalFontSize);
      const familyChanged    = hasFamilySnap && edit.fontFamily != null && String(edit.fontFamily) !== String(edit._originalFontFamily);
      const boldChanged      = hasBoldSnap   && !!edit.bold      !== !!edit._originalBold;
      const italicChanged    = hasItalicSnap && !!edit.italic    !== !!edit._originalItalic;
      const underlineChanged = hasUnderSnap  && !!edit.underline !== !!edit._originalUnderline;
      // textColor/bgColor/alignment have harmless defaults; the snapshot diff
      // is what keeps formatChanged from being truthy on every block opened.
      const colorChanged     = hasColorSnap  && edit.textColor   != null && String(edit.textColor) !== String(edit._originalTextColor);
      const bgColorChanged   = hasBgSnap     && edit.bgColor     != null && String(edit.bgColor)   !== String(edit._originalBgColor);
      const alignmentChanged = hasAlignSnap  && edit.alignment   != null && String(edit.alignment) !== String(edit._originalAlignment);

      // `runsArr` is freshly extracted from the live contentEditable, so it
      // always reflects the current text + formatting. Sending it whenever
      // it's present keeps the server's stored runs in lockstep with text;
      // uniform run arrays still normalize to nil server-side via
      // normalize_edit_runs so the cheap single-Tj branch wins when appropriate.
      const runsArr = Array.isArray(edit.runs) ? edit.runs : null;

      const formatChanged = sizeChanged || familyChanged || boldChanged ||
        italicChanged || underlineChanged ||
        colorChanged || bgColorChanged || alignmentChanged;
      const isActualEdit = edit.edited === true || textChanged || formatChanged;
      if (!isActualEdit) return;
      const entry = {
        text: edit.text != null ? edit.text : (edit.pdfOriginalText || edit.originalText || ""),
        identity: blockIdentity(edit)
      };
      // Only emit fields the user actually changed. The server treats any present
      // field as an explicit user request and will run the neighbor clamp / inject
      // path accordingly — sending unchanged values causes the bug we're fixing.
      if (familyChanged)    entry.fontFamily = edit.fontFamily;
      if (sizeChanged)      entry.fontSize   = Number(edit.fontSize);
      if (boldChanged)      entry.bold       = !!edit.bold;
      if (italicChanged)    entry.italic     = !!edit.italic;
      if (underlineChanged) entry.underline  = !!edit.underline;
      if (runsArr && runsArr.length > 0) entry.runs = runsArr;
      if (colorChanged)     entry.textColor  = edit.textColor;
      if (bgColorChanged)   entry.bgColor    = edit.bgColor;
      if (alignmentChanged) entry.alignment  = edit.alignment;
      if (edit.fontName) entry.fontName = edit.fontName;
      if (edit.originalText) entry.originalText = edit.originalText;
      if (edit.pdfOriginalText) entry.pdfOriginalText = edit.pdfOriginalText;
      if (edit.transform) entry.transform = edit.transform;
      if (edit.firstItemIndex != null) entry.firstItemIndex = edit.firstItemIndex;
      if (edit.height) entry.height = edit.height;
      if (edit.width) entry.width = edit.width;
      if (edit.x != null) entry.x = edit.x;
      if (edit.y != null) entry.y = edit.y;
      edits[blockIndex] = entry;
    });

    const viewport = editorState.pageTextContent?.viewport;
    const viewportInfo = viewport ? {
      width: viewport.width,
      height: viewport.height,
      scale: editorState.scale || viewport.scale || 1.0
    } : null;

    if (DEBUG_TEXT_BLOCKS) {
      debugLog("[pdf_editor][DEBUG_TEXT_BLOCKS] Serialize payload:", {
        shape: Object.keys(edits).length ? "edits" : "edits_empty",
        page,
        editedIndices: Object.keys(edits).map(Number),
        hasBaselineBlocks: textBlocksFromPdf.length > 0
      });
    }

    const doPut = (payload) => {
      const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
      const queryParams = new URLSearchParams({ page: String(page) });
      const url = `${editorState.textEditsUrl}?${queryParams}`;
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        if (showProgress && dataUploadProgressBar) {
          xhr.upload.addEventListener("progress", (evt) => {
            if (evt.lengthComputable) {
              const percentComplete = Math.floor((evt.loaded / evt.total) * 100);
              dataUploadProgressBar.style.width = percentComplete + "%";
              dataUploadProgressBar.setAttribute("aria-valuenow", percentComplete);
            }
          }, false);
        }

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            if (showProgress && dataUploadProgressBar) {
              dataUploadProgressBar.style.width = "100%";
              dataUploadProgressBar.setAttribute("aria-valuenow", "100");
            }
            let body;
            try {
              body = JSON.parse(xhr.responseText || "{}");
            } catch (_) {
              body = {};
            }
            if (body.need_baseline && textBlocksFromPdf.length > 0) {
              debugLog(`[pdf_editor] Server needs baseline for page ${page}, retrying with baseline_blocks`);
              const baselinePayload = {
                page,
                baseline_blocks: textBlocksFromPdf.map((block, index) => ({
                  ...block,
                  index,
                  pdfOriginalText: block.text || "",
                  originalText: block.text || ""
                })),
                edits,
                viewport: viewportInfo
              };
              doPut(baselinePayload).then(resolve).catch(reject);
              return;
            }
            debugLog(`[pdf_editor] Successfully saved text edits for page ${page}`);
            if (scheduleReloadAfterSave && (Object.keys(edits).length > 0 || (payload.baseline_blocks && payload.baseline_blocks.length > 0))) {
              reloadPdfAfterRebuild(page, { showProgress });
            } else {
              _textEditSaveInFlight = false;
              setSavingUI(false);
              if (showProgress) {
                setTimeout(() => {
                  if (dataUploadProgressBar) {
                    dataUploadProgressBar.style.width = "0%";
                    dataUploadProgressBar.setAttribute("aria-valuenow", "0");
                  }
                  if (previewRebuildProgressBar) {
                    previewRebuildProgressBar.style.width = "0%";
                    previewRebuildProgressBar.setAttribute("aria-valuenow", "0");
                  }
                  if (dataUploadProgressLabel) dataUploadProgressLabel.textContent = "Data Upload Progress";
                  if (previewRebuildProgressLabel) previewRebuildProgressLabel.textContent = "Preview Upload Progress";
                }, 1500);
              }
            }
            resolve(xhr.response);
          } else {
            console.warn("[pdf_editor] Text edits PUT failed", xhr.status, xhr.responseText);
            if (showProgress) resetProgressBars();
            _textEditSaveInFlight = false;
            setSavingUI(false);
            reject(new Error(`Text edits PUT failed: ${xhr.status}`));
          }
        });
        xhr.addEventListener("error", () => {
          console.error("[pdf_editor] Text edits PUT network error");
          if (showProgress) resetProgressBars();
          _textEditSaveInFlight = false;
          setSavingUI(false);
          reject(new Error("Text edits PUT failed: network error"));
        });
        xhr.open("PUT", url);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");
        if (csrf) xhr.setRequestHeader("X-CSRF-Token", csrf);
        xhr.withCredentials = true;
        const toSend = { ...payload, viewport: viewportInfo, request_id: requestId, comments: comment };
        if (!toSend.viewport) delete toSend.viewport;
        debugLog(`[pdf_editor] PUT ${url}`, Object.keys(toSend));
        xhr.send(JSON.stringify(toSend));
      });
    };

    try {
      if (textBlocksFromPdf.length > 0 && Object.keys(edits).length > 0) {
        return doPut({ page, edits });
      }
      if (textBlocksFromPdf.length > 0 && (pageEdits.length === 0 || flushedIndices instanceof Set)) {
        return doPut({ page, edits: {} });
      }
      // No PDF text content: send legacy full list so we don't lose data (server will migrate to baseline).
      const payloadBlocks = textBlocksFromPdf.length > 0
        ? textBlocksFromPdf.map((block, index) => {
            const edit = pageEdits.find((e) => (e.index != null ? e.index : pageEdits.indexOf(e)) === index);
            if (edit) return edit;
            return { ...block, index, pdfOriginalText: block.text || "", originalText: block.text || "" };
          })
        : pageEdits;
      return doPut({ page, text_blocks: payloadBlocks });
    } catch (e) {
      _textEditSaveInFlight = false;
      console.error("[pdf_editor] saveTextEdits failed", e);
      if (typeof window !== "undefined" && window.toastr && typeof window.toastr.error === "function") {
        window.toastr.error("Text edit save failed: " + (e && e.message ? e.message : "unknown error") + ". Please retry.");
      }
      throw e;
    }
  }

  async function loadTextEdits(page) {
    if (!editorState.textEditsUrl) {
      debugLog(`[pdf_editor] loadTextEdits: no textEditsUrl for page ${page}`);
      return;
    }
    
    try {
      const queryParams = new URLSearchParams({ page: String(page) });
      queryParams.set("_cb", Date.now().toString());
      const url = `${editorState.textEditsUrl}?${queryParams}`;
      debugLog(`[pdf_editor] Loading text edits for page ${page} from ${url}`);
      const res = await fetch(url, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
        cache: "no-store",
      });
      if (!res.ok) {
        console.warn(`[pdf_editor] loadTextEdits: HTTP ${res.status} for page ${page}`);
        editorState.textEdits[page] = [];
        return;
      }
      const data = await res.json();
      if (data && Array.isArray(data.text_blocks)) {
        editorState.textEdits[page] = data.text_blocks;
        // The response is the merged baseline + edits, so most blocks are unchanged
        // baseline carriers — only those with edited:true reflect a user save.
        // Distinguish them so the log can't be misread as "old data persisting".
        const editedCount = data.text_blocks.reduce((n, b) => n + (b && b.edited === true ? 1 : 0), 0);
        debugLog(`[pdf_editor] Loaded ${data.text_blocks.length} text blocks for page ${page} (${editedCount} edited)`);
        if (data.text_blocks.length === 0) {
          debugLog(`[pdf_editor] Edit Text mode will use PDF.js-extracted text for page ${page} (no saved edits for this page yet)`);
        }
      } else {
        debugLog(`[pdf_editor] No stored text edits for page ${page}; Edit Text will use PDF.js text layer only`);
        editorState.textEdits[page] = [];
      }
    } catch (e) {
      console.warn("[pdf_editor] loadTextEdits failed", e);
    }
  }

  // Renders a PDF page to the base canvas and loads its annotations
  async function renderPage(num) {
    editorState.rendering = true;
    syncEditorControls();

    // Cancel any active text editing before page change to prevent stale references
    const editor = getTextEditor();
    if (editor && editor.isEditing()) {
      editor.cancelEditing();
    }

    const loadingIndicator = document.createElement("div");
    loadingIndicator.id = "pdf-loading-indicator";
    loadingIndicator.style.cssText = "position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); z-index:1000; background:rgba(255,255,255,0.9); padding:20px; border-radius:8px; box-shadow:0 4px 6px rgba(0,0,0,0.1);";
    loadingIndicator.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Loading page...';
    if (container) container.appendChild(loadingIndicator);

    let pageRenderSucceeded = false;
    try {
      await cancelInFlightRender();

      if (num < 1) {
        throw new Error(`Invalid page number: ${num}`);
      }

      canvas.clear();
      canvas.renderAll();
      editorState.history = [];
      editorState.future = [];
      // Reset text edit restriction — will be re-evaluated for this page
      editorState.pageTextEditDisabled = false;
      editorState.pageTextEditDisabledReason = null;

      const doc = await ensurePdfDoc();
      
      if (!doc || !doc.numPages) {
        throw new Error("Failed to load PDF document");
      }
      
      editorState.pageCount = doc.numPages;
      
      if (num > doc.numPages) {
        console.warn(`Page ${num} requested but PDF only has ${doc.numPages} pages. Using page ${doc.numPages}.`);
        num = doc.numPages;
        editorState.pageNum = num;
      }
      
      if (pageCountEl) pageCountEl.textContent = String(doc.numPages);

      const page = await doc.getPage(num);
      const rawViewport = page.getViewport({ scale: 1 });

      // Dynamic per-page scale: fit page width to the available container width
      // baseScale fits width, capped so normal pages don't become too large.
      // userZoom allows manual zoom adjustment.
      const availableWidth = sizer.clientWidth - 20;
      if (availableWidth > 0 && rawViewport.width > 0) {
        const fitScale = availableWidth / rawViewport.width;
        const baseScale = Math.min(Math.max(fitScale, 0.5), 2.0);
        editorState.scale = baseScale * editorState.userZoom;
      }
      const viewport = page.getViewport({ scale: editorState.scale });

      resetTransforms();
      syncOverlaySize(viewport.width, viewport.height);
      baseCtx.clearRect(0, 0, baseCanvas.width, baseCanvas.height);

      const task = page.render({ canvasContext: baseCtx, viewport });
      editorState.renderTask = task;
      await task.promise;

      const COMPLEX_PAGE_THRESHOLDS = {
        maxPageWidth: 2000,
        maxPageHeight: 2000,
        maxPageArea: 3000000,
        maxTextBlocks: 80,
        maxImageAreaRatio: 0.85
      };
      const BLOCKED_MSG = "This page is too large or complex for reliable text editing. Annotations are still allowed.";
      try {
        const pageWidth = rawViewport.width;
        const pageHeight = rawViewport.height;
        const pageArea = pageWidth * pageHeight;
        let complexPageReason = null;
        const debugInfo = { page: num, width: Math.round(pageWidth), height: Math.round(pageHeight), area: Math.round(pageArea) };

        // --- Size checks ---
        if (pageWidth > COMPLEX_PAGE_THRESHOLDS.maxPageWidth ||
            pageHeight > COMPLEX_PAGE_THRESHOLDS.maxPageHeight ||
            pageArea > COMPLEX_PAGE_THRESHOLDS.maxPageArea) {
          complexPageReason = BLOCKED_MSG;
          debugInfo.sizeBlocked = true;
        }

        // --- Image ratio check ---
        if (!complexPageReason) {
          try {
            const opList = await page.getOperatorList();
            let totalImageArea = 0;
            let imageCount = 0;
            const OPS = window.pdfjsLib.OPS;
            for (let i = 0; i < opList.fnArray.length; i++) {
              const op = opList.fnArray[i];
              if (op === OPS.paintImageXObject || op === OPS.paintImageXObjectRepeat) {
                const args = opList.argsArray[i];
                if (args && args.length >= 3 && typeof args[1] === 'number' && typeof args[2] === 'number') {
                  totalImageArea += Math.max(0, args[1]) * Math.max(0, args[2]);
                  imageCount++;
                }
              } else if (op === OPS.paintInlineImageXObject) {
                const args = opList.argsArray[i];
                if (args && args[0]) {
                  totalImageArea += Math.max(0, args[0].width || 0) * Math.max(0, args[0].height || 0);
                  imageCount++;
                }
              }
            }
            const imageAreaRatio = pageArea > 0 ? totalImageArea / pageArea : 0;
            debugInfo.imageCount = imageCount;
            debugInfo.imageAreaRatio = (imageAreaRatio * 100).toFixed(1) + "%";
            if (imageAreaRatio > COMPLEX_PAGE_THRESHOLDS.maxImageAreaRatio) {
              complexPageReason = BLOCKED_MSG;
              debugInfo.imageBlocked = true;
            }
          } catch (opErr) {
            console.warn("[pdf_editor] Failed to analyze page operators:", opErr);
          }
        }

        // --- Text content analysis ---
        const textContent = await page.getTextContent({ normalizeWhitespace: false });
        const textItems = extractTextItems(textContent, viewport);
        debugInfo.rawItems = (textContent && textContent.items) ? textContent.items.length : 0;
        debugInfo.extractedItems = textItems ? textItems.length : 0;

        if (!complexPageReason && (!textItems || textItems.length === 0)) {
          editorState.pageTextEditDisabled = true;
          editorState.pageTextEditDisabledReason = "no_text";
          debugInfo.noText = true;
        }

        if (!complexPageReason && textItems && textItems.length > 0) {
          const testBlocks = groupTextItems(textItems);
          debugInfo.textBlocks = testBlocks.length;

          // Block count check
          if (testBlocks.length > COMPLEX_PAGE_THRESHOLDS.maxTextBlocks) {
            complexPageReason = BLOCKED_MSG;
            debugInfo.blockCountBlocked = true;
          }

          // Checkbox glyph check
          if (!complexPageReason) {
            const checkboxGlyphs = /[\u2610\u2611\u2612\u2713\u2714\u2717\u2718☐☑☒✓✔✗✘]/;
            let hasCheckbox = false;
            for (const item of textItems) {
              if (checkboxGlyphs.test(item.str || '')) { hasCheckbox = true; break; }
            }
            debugInfo.checkboxes = hasCheckbox;
            if (hasCheckbox) {
              complexPageReason = BLOCKED_MSG;
              debugInfo.checkboxBlocked = true;
            }
          }

          // Table / column layout detection
          if (!complexPageReason && testBlocks.length >= 6) {
            const yTolerance = 6;
            const rows = [];
            const sorted = [...testBlocks].sort((a, b) => a.y - b.y);
            sorted.forEach(block => {
              const lastRow = rows[rows.length - 1];
              if (lastRow && Math.abs(block.y - lastRow.y) < yTolerance) {
                lastRow.items.push(block);
              } else {
                rows.push({ y: block.y, items: [block] });
              }
            });
            const multiItemRows = rows.filter(r => r.items.length >= 3);
            debugInfo.totalRows = rows.length;
            debugInfo.multiItemRows = multiItemRows.length;

            // If >= 4 rows each contain 3+ aligned blocks, it looks like a table
            if (multiItemRows.length >= 4) {
              complexPageReason = BLOCKED_MSG;
              debugInfo.tableBlocked = true;
            }
          }
        }

        if (complexPageReason) {
          editorState.pageTextEditDisabled = true;
          editorState.pageTextEditDisabledReason = complexPageReason;
        }

        // Consolidated debug log
        debugInfo.editingBlocked = editorState.pageTextEditDisabled;
        debugLog("[ComplexPageDetector]", JSON.stringify(debugInfo));
        editorState.pageTextContent = {
          items: textContent.items,
          viewport: viewport,
          textItems: textItems
        };
      } catch (err) {
        console.warn("[pdf_editor] Failed to load text content for page " + num, err);
        editorState.pageTextContent = null;
        editorState.pageTextEditDisabled = true;
      }

      if (pageNumEl) pageNumEl.textContent = String(num);
      if (pageNumInput) pageNumInput.value = String(num);

      renderTextLayer();

      await loadAnnotations(num);
      pageRenderSucceeded = true;
    } catch (err) {
      const isCancelled = err?.name === "RenderingCancelledException" || err?.message?.includes?.("Rendering cancelled");
      if (isCancelled) {
        debugLog("[pdf_editor] Render cancelled (expected when starting new render)");
      } else {
        console.error("[pdf_editor] render error", err);
        const errorMsg = document.createElement("div");
        errorMsg.style.cssText = "position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); z-index:1000; background:#fee; padding:20px; border-radius:8px; border:2px solid #f00; max-width:400px; text-align:center;";
        const escapedMessage = (err.message || "Unknown error").replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        errorMsg.innerHTML = `<strong>Error loading PDF</strong><br>${escapedMessage}<br><button onclick="location.reload()" style="margin-top:10px; padding:8px 16px; background:#007bff; color:white; border:none; border-radius:4px; cursor:pointer;">Reload Page</button>`;
        if (container) {
          const existing = container.querySelector("#pdf-loading-indicator");
          if (existing) existing.remove();
          container.appendChild(errorMsg);
        }
      }
    } finally {
      const loadingIndicator = container?.querySelector("#pdf-loading-indicator");
      if (loadingIndicator) loadingIndicator.remove();
      
      editorState.renderTask = null;
      editorState.rendering = false;
      if (pageRenderSucceeded && container?.dataset.editorReady !== "true") {
        container.dataset.editorReady = "true";
      }
      syncEditorControls();
      if (editorState.renderPending) {
        editorState.renderPending = false;
        renderPage(editorState.pageNum);
      }
    }
  }

  // Queues a page render, deferring if a render is already in progress
  function queueRenderPage(num) {
    if (editorState.rendering) {
      editorState.renderPending = true;
    } else {
      renderPage(num);
    }
  }

  // Updates the settings panel UI with properties from the selected object
  function updateSettingsPanelForObject(obj) {
    if (!obj) return;
    
    const supportsEditing = obj.type === "path" || 
                           obj.type === "line" || 
                           obj.type === "rect" || 
                           obj.type === "ellipse" ||
                           (obj.type === "group" && obj._objects && obj._objects.length > 0);
    
    if (supportsEditing) {
      const props = extractObjectProperties(obj);
      
      showSettingsPanel(true);
      
      if (toolColorPicker) toolColorPicker.value = props.color;
      if (toolColorHex) toolColorHex.value = props.color;
      if (toolOpacitySlider) toolOpacitySlider.value = props.opacity;
      if (toolOpacityValue) toolOpacityValue.textContent = props.opacity.toFixed(1);
      const toolOpacityInput = document.getElementById("tool-opacity-input");
      if (toolOpacityInput) toolOpacityInput.value = props.opacity;
      if (toolSizeSlider) toolSizeSlider.value = props.size;
      if (toolSizeValue) toolSizeValue.textContent = props.size;
      const toolSizeInput = document.getElementById("tool-size-input");
      if (toolSizeInput) toolSizeInput.value = props.size;
      
      if (toolSettingsMode) {
        const typeNames = {
          "path": obj.strokeWidth > 10 ? "Highlighter" : "Free Draw",
          "line": "Line",
          "rect": "Rectangle",
          "ellipse": "Ellipse",
          "group": "Group"
        };
        toolSettingsMode.textContent = typeNames[obj.type] || "Selected Object";
      }
    }
  }

  let stickyTools = false;
  // Adds an object to the canvas, selects it, and updates UI (called after placing new objects)
  function afterPlace(obj) {
    editorState.suppressHistory = true;
    canvas.add(obj).setActiveObject(obj);
    editorState.suppressHistory = false;
    canvas.requestRenderAll();
    snapshot();
    
    if (!stickyTools) setMode("select");
    
    requestAnimationFrame(() => {
      const activeObj = canvas.getActiveObject();
      if (activeObj === obj) {
        updateSettingsPanelForObject(obj);
      }
    });
  }

  // Sets the current tool mode and updates canvas behavior accordingly
  function setMode(newMode) {
    if (isEditorToolbarLocked(editorState)) return;

    // Block text editing on complex/large pages — annotations remain allowed
    if (editorState.pageTextEditDisabled && newMode === "edit-text") {
      const reason = editorState.pageTextEditDisabledReason || "";
      const msg = reason === "no_text"
        ? "This page has no editable text. Annotations are still allowed."
        : "This page contains tables or complex layouts and cannot be safely text edited. Annotations are still allowed.";
      if (typeof toastr !== "undefined") {
        toastr.warning(msg, "", { timeOut: 4000 });
      } else {
        alert(msg);
      }
      console.warn(`[PDF_EDIT_BLOCKED] page=${editorState.pageNum} reason=${reason}`);
      return;
    }

    if (lineStart || lineTemp) {
      if (lineTemp) canvas.remove(lineTemp);
      lineStart = null;
      lineTemp = null;
      canvas.renderAll();
    }
    if (highlighterStart || highlighterTemp) {
      if (highlighterTemp) canvas.remove(highlighterTemp);
      highlighterStart = null;
      highlighterTemp = null;
      canvas.renderAll();
    }
    
    const previousMode = editorState.mode;
    editorState.mode = newMode;
    canvas.isDrawingMode = newMode === "free-draw";
    canvas.skipTargetFind = newMode === "pan";
    canvas.selection = newMode === "select";
    canvas.defaultCursor =
      newMode === "pan" ? "grab" : newMode === "eraser" ? "not-allowed" : "default";
    
    const showSettings = ["pen-highlight", "free-draw", "rect", "ellipse", "arrow", "line"].includes(newMode);
    showSettingsPanel(showSettings);
    
    if (showSettings) {
      updateToolSettingsUI(newMode);
    }
    
    if (newMode === "free-draw") {
      setFreeDrawPen();
    }
    
    debugLog("[pdf_editor] setMode called with:", newMode, "textLayer exists:", !!textLayer);
    if (textLayer) {
      if (newMode === "edit-text") {
        debugLog("[pdf_editor] Entering edit-text mode");
        if (editorState.pageTextContent && editorState.pageTextContent.textItems) {
          debugLog("[pdf_editor] Text content exists, rendering text layer");
          renderTextLayer();
        } else {
          debugLog("[pdf_editor] No text content available");
        }
        if (textLayer) {
          textLayer.style.setProperty('visibility', 'visible', 'important');
          textLayer.style.pointerEvents = "auto";
          textLayer.style.zIndex = "100";
          debugLog("[pdf_editor] Text layer visibility set to visible, computed:", window.getComputedStyle(textLayer).visibility);
        }
        // Fully hide base canvas and clear its content so original PDF text is not visible or clickable (fixes non-Prawn PDFs)
        baseCanvas.style.display = "none";
        baseCanvas.style.visibility = "hidden";
        baseCanvas.style.opacity = "0";
        baseCanvas.style.pointerEvents = "none";
        baseCanvas.style.zIndex = "-1";
        try {
          baseCtx.clearRect(0, 0, baseCanvas.width, baseCanvas.height);
        } catch (_) {}
        if (canvasWrapper) {
          canvasWrapper.style.backgroundColor = "#ffffff";
          canvasWrapper.style.width = baseCanvas.width + "px";
          canvasWrapper.style.height = baseCanvas.height + "px";
        }
        if (editModeOverlay) editModeOverlay.style.display = "block";
        canvas.skipTargetFind = true;
        canvas.selection = false;
        canvas.interactive = false;
        canvas.defaultCursor = "text";
        if (overlayCanvas) {
          overlayCanvas.style.pointerEvents = "none";
          overlayCanvas.style.zIndex = "1";
        }
        if (fabricContainerEl) {
          fabricContainerEl.style.display = "none";
        }
        if (textEditingControls) {
          textEditingControls.style.display = "block";
          debugLog("[pdf_editor] Text editing controls shown");
        } else {
          console.warn("[pdf_editor] Text editing controls element not found");
        }
        if (btnEditText) {
          const walker = document.createTreeWalker(btnEditText, NodeFilter.SHOW_TEXT, null, false);
          let textNode;
          let found = false;
          while (textNode = walker.nextNode()) {
            const trimmed = textNode.textContent.trim();
            if (trimmed === "Edit Text" || trimmed.includes("Edit Text")) {
              textNode.textContent = textNode.textContent.replace(/Edit Text/g, "Save Changes");
              found = true;
              break;
            }
          }
          const icon = btnEditText.querySelector('i');
          if (icon) {
            icon.className = "fas fa-save pr-2";
          }
          btnEditText.classList.add("active");
          btnEditText.style.backgroundColor = "#28a745";
          btnEditText.style.borderColor = "#28a745";
          btnEditText.style.color = "#fff";
        }
        if (btnCancelTextEdits) btnCancelTextEdits.style.display = "";
      } else {
        if (textLayer) {
          textLayer.style.visibility = "hidden";
          textLayer.style.pointerEvents = "none";
        }
        baseCanvas.style.display = "";
        baseCanvas.style.visibility = "visible";
        baseCanvas.style.opacity = "";
        baseCanvas.style.pointerEvents = "";
        baseCanvas.style.zIndex = "";
        if (canvasWrapper) {
          canvasWrapper.style.backgroundColor = "";
          canvasWrapper.style.width = "";
          canvasWrapper.style.height = "";
        }
        if (editModeOverlay) editModeOverlay.style.display = "none";
        if (textEditingControls) textEditingControls.style.display = "none";
        if (btnCancelTextEdits) btnCancelTextEdits.style.display = "none";
        // Re-render page to restore canvas content only when exiting edit mode (avoid cancelling initial render)
        if (previousMode === "edit-text") {
          queueRenderPage(editorState.pageNum);
        }
        if (btnEditText) {
          const walker = document.createTreeWalker(btnEditText, NodeFilter.SHOW_TEXT, null, false);
          let textNode;
          while (textNode = walker.nextNode()) {
            if (textNode.textContent.includes("Save Changes")) {
              textNode.textContent = textNode.textContent.replace("Save Changes", "Edit Text");
              break;
            }
          }
          const icon = btnEditText.querySelector('i');
          if (icon) {
            icon.className = "fas fa-edit pr-2";
          }
          btnEditText.classList.remove("active");
          btnEditText.style.backgroundColor = "";
          btnEditText.style.borderColor = "";
          btnEditText.style.color = "";
        }
        // Restore canvas state based on new mode
        canvas.interactive = newMode !== "pan";
        canvas.skipTargetFind = newMode === "pan";
        canvas.selection = newMode === "select";
        if (overlayCanvas) {
          overlayCanvas.style.pointerEvents = "auto";
          overlayCanvas.style.zIndex = "10";
        }
        if (fabricContainerEl) {
          fabricContainerEl.style.display = "";
          fabricContainerEl.style.pointerEvents = "auto";
          fabricContainerEl.style.zIndex = "10";
          fabricContainerEl.style.opacity = "1";
        }
        const editor = getTextEditor();
        if (editor && editor.isEditing()) {
          editor.cancelEditing();
        }
      }
    }
    syncEditorControls();
  }

  // Updates the tool settings UI controls with values for the specified mode
  function updateToolSettingsUI(mode) {
    const settings = toolSettings[mode] || toolSettings["pen-highlight"];
    if (toolColorPicker) toolColorPicker.value = settings.color;
    if (toolColorHex) toolColorHex.value = settings.color;
    if (toolOpacitySlider) toolOpacitySlider.value = settings.opacity;
    if (toolOpacityValue) toolOpacityValue.textContent = settings.opacity.toFixed(1);
    if (toolOpacityInput) toolOpacityInput.value = settings.opacity;
    if (toolSizeSlider) toolSizeSlider.value = settings.size;
    if (toolSizeValue) toolSizeValue.textContent = settings.size;
    if (toolSizeInput) toolSizeInput.value = settings.size;
    if (toolSettingsMode) {
      const modeNames = {
        "pen-highlight": "Highlighter",
        "free-draw": "Free Draw",
        "rect": "Rectangle",
        "ellipse": "Ellipse",
        "line": "Strikeline / Underline"
      };
      toolSettingsMode.textContent = modeNames[mode] || "";
    }
  }

  // Returns the current tool's settings (color, opacity, size)
  function getCurrentToolSettings() {
    const mode = editorState.mode;
    return toolSettings[mode] || toolSettings["pen-highlight"];
  }

  // Updates tool settings, or applies to selected object if in select mode
  function updateCurrentToolSettings(color, opacity, size) {
    const mode = editorState.mode;
    
    const activeObj = canvas.getActiveObject();
    if (activeObj && editorState.mode === "select") {
      updateSelectedObject(color, opacity, size);
      return;
    }
    
    if (toolSettings[mode]) {
      toolSettings[mode].color = color;
      toolSettings[mode].opacity = opacity;
      toolSettings[mode].size = size;
    }
    
    if (mode === "pen-highlight") {
      setHighlighterPen();
    } else if (mode === "free-draw") {
      setFreeDrawPen();
    }
  }

  function getCurrentUIValues() {
    const color = toolColorPicker ? toolColorPicker.value : "#000000";
    const opacity = toolOpacitySlider ? parseFloat(toolOpacitySlider.value) : 1.0;
    const size = toolSizeSlider ? parseInt(toolSizeSlider.value) : 3;
    return { color, opacity, size };
  }

  let panelNaturalHeight = null;
  function showSettingsPanel(show) {
    if (!toolSettingsPanel) return;
    
    if (panelNaturalHeight === null) {
      const originalVisibility = toolSettingsPanel.style.visibility;
      const originalHeight = toolSettingsPanel.style.height;
      const originalOverflow = toolSettingsPanel.style.overflow;
      
      toolSettingsPanel.style.visibility = "visible";
      toolSettingsPanel.style.height = "auto";
      toolSettingsPanel.style.overflow = "visible";
      panelNaturalHeight = toolSettingsPanel.scrollHeight;
      
      toolSettingsPanel.style.visibility = originalVisibility;
      toolSettingsPanel.style.height = originalHeight;
      toolSettingsPanel.style.overflow = originalOverflow;
    }
    
    if (panelNaturalHeight !== null) {
      toolSettingsPanel.style.height = panelNaturalHeight + "px";
      
      if (show) {
        toolSettingsPanel.style.visibility = "visible";
        toolSettingsPanel.style.overflow = "visible";
      } else {
        toolSettingsPanel.style.visibility = "hidden";
        toolSettingsPanel.style.overflow = "hidden";
      }
    }
  }

  const styles = {
    highlightStroke: "rgba(255, 255, 0, 0.9)",
    highlightFill: "rgba(255, 255, 0, 0.25)",
    textFill: "#111",
    textFontSize: 16,
    ellipseStroke: "rgba(0,0,255,0.9)",
    ellipseFill: "rgba(0,0,255,0.15)",
    checkboxSize: 18,
  };

  function hexToRgba(hex, opacity) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  function rgbaToHex(rgba) {
    if (!rgba || rgba === "transparent") return "#000000";
    if (rgba.startsWith("#")) return rgba;
    
    const match = rgba.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    if (match) {
      const r = parseInt(match[1]).toString(16).padStart(2, "0");
      const g = parseInt(match[2]).toString(16).padStart(2, "0");
      const b = parseInt(match[3]).toString(16).padStart(2, "0");
      return `#${r}${g}${b}`;
    }
    return "#000000";
  }

  function extractOpacityFromRgba(rgba) {
    if (!rgba || rgba === "transparent") return 1.0;
    const match = rgba.match(/rgba?\([\d\s,]+,\s*([\d.]+)\)/);
    if (match) {
      return parseFloat(match[1]);
    }
    return 1.0;
  }

  // Extracts color, opacity, and size properties from a canvas object
  function extractObjectProperties(obj) {
    let color = "#000000";
    let opacity = 1.0;
    let size = 3;
    
    if (!obj) return { color, opacity, size };
    
    const objOpacity = obj.opacity !== undefined ? obj.opacity : 1.0;
    
    if (obj.type === "group" && obj._objects) {
      const firstObj = obj._objects[0];
      if (firstObj) {
        const strokeOrFill = firstObj.stroke || firstObj.fill || "#000000";
        color = rgbaToHex(strokeOrFill);
        opacity = extractOpacityFromRgba(strokeOrFill);
        if (opacity === 1.0 && firstObj.opacity !== undefined) {
          opacity = firstObj.opacity;
        }
        size = firstObj.strokeWidth || 3;
      }
      return { color, opacity, size };
    }
    
    if (obj.type === "path") {
      const stroke = obj.stroke || "#000000";
      color = rgbaToHex(stroke);
      opacity = extractOpacityFromRgba(stroke);
      if (opacity === 1.0 && objOpacity !== 1.0) {
        opacity = objOpacity;
      }
      size = obj.strokeWidth || 3;
      return { color, opacity, size };
    }
    
    if (obj.type === "line") {
      const stroke = obj.stroke || "#000000";
      color = rgbaToHex(stroke);
      opacity = extractOpacityFromRgba(stroke);
      if (opacity === 1.0 && objOpacity !== 1.0) {
        opacity = objOpacity;
      }
      size = obj.strokeWidth || 3;
      return { color, opacity, size };
    }
    
    if (obj.type === "rect") {
      const fillColor = obj.fill && obj.fill !== "transparent" ? obj.fill : null;
      const strokeColor = obj.stroke && obj.stroke !== "transparent" ? obj.stroke : null;
      const colorSource = fillColor || strokeColor || "#000000";
      color = rgbaToHex(colorSource);
      opacity = extractOpacityFromRgba(colorSource);
      if (opacity === 1.0 && objOpacity !== 1.0) {
        opacity = objOpacity;
      }
      if (!obj._baseWidth) {
        obj._baseWidth = obj.width;
        obj._baseHeight = obj.height;
        obj._baseStrokeWidth = obj.strokeWidth || 2;
      }
      if (obj._originalSize !== undefined) {
        const baseAvgSizePixels = (obj._baseWidth + obj._baseHeight) / 2;
        const currentAvgSizePixels = (obj.width * (obj.scaleX || 1) + obj.height * (obj.scaleY || 1)) / 2;
        const scaleFactor = baseAvgSizePixels > 0 ? currentAvgSizePixels / baseAvgSizePixels : 1;
        const originalSizePixels = sizeToPixels(obj._originalSize);
        const currentSizePixels = originalSizePixels * scaleFactor;
        size = pixelsToSize(currentSizePixels);
      } else {
        const currentAvgSizePixels = (obj.width * (obj.scaleX || 1) + obj.height * (obj.scaleY || 1)) / 2;
        size = pixelsToSize(currentAvgSizePixels);
        obj._originalSize = size;
      }
      return { color, opacity, size };
    }
    
    if (obj.type === "ellipse") {
      const fillColor = obj.fill && obj.fill !== "transparent" ? obj.fill : null;
      const strokeColor = obj.stroke && obj.stroke !== "transparent" ? obj.stroke : null;
      const colorSource = fillColor || strokeColor || "#000000";
      color = rgbaToHex(colorSource);
      opacity = extractOpacityFromRgba(colorSource);
      if (opacity === 1.0 && objOpacity !== 1.0) {
        opacity = objOpacity;
      }
      if (!obj._baseRx) {
        obj._baseRx = obj.rx;
        obj._baseRy = obj.ry;
        obj._baseStrokeWidth = obj.strokeWidth || 2;
      }
      if (obj._originalSize !== undefined) {
        const baseAvgSizePixels = (obj._baseRx + obj._baseRy) / 2;
        const currentAvgSizePixels = (obj.rx * (obj.scaleX || 1) + obj.ry * (obj.scaleY || 1)) / 2;
        const scaleFactor = baseAvgSizePixels > 0 ? currentAvgSizePixels / baseAvgSizePixels : 1;
        const originalSizePixels = sizeToPixels(obj._originalSize);
        const currentSizePixels = originalSizePixels * scaleFactor;
        size = pixelsToSize(currentSizePixels);
      } else {
        const currentAvgSizePixels = (obj.rx * (obj.scaleX || 1) + obj.ry * (obj.scaleY || 1)) / 2;
        size = pixelsToSize(currentAvgSizePixels);
        obj._originalSize = size;
      }
      return { color, opacity, size };
    }
    
    return { color, opacity, size };
  }

  // Updates the currently selected object with new color, opacity, and size
  function updateSelectedObject(color, opacity, size) {
    const activeObj = canvas.getActiveObject();
    if (!activeObj) return;
    
    const colorRgba = hexToRgba(color, opacity);
    
    if (activeObj.type === "group" && activeObj._objects) {
      activeObj._objects.forEach(child => {
        if (child.stroke) child.set("stroke", colorRgba);
        if (child.fill) {
          if (child.type === "triangle" || child.type === "polyline") {
            child.set("fill", colorRgba);
          } else if (child.type !== "rect" && child.type !== "ellipse") {
            child.set("fill", colorRgba);
          }
        }
        if (child.strokeWidth !== undefined) child.set("strokeWidth", size);
        child.set("opacity", opacity);
      });
      activeObj.set("opacity", opacity);
      canvas.renderAll();
      snapshot();
      return;
    }
    
    if (activeObj.type === "path") {
      activeObj.set({
        stroke: colorRgba,
        strokeWidth: size,
        opacity: opacity
      });
      canvas.renderAll();
      snapshot();
      return;
    }
    
    if (activeObj.type === "line") {
      activeObj.set({
        stroke: colorRgba,
        strokeWidth: size,
        opacity: opacity
      });
      canvas.renderAll();
      snapshot();
      return;
    }
    
    if (activeObj.type === "rect") {
      if (!activeObj._baseWidth) {
        activeObj._baseWidth = activeObj.width * (activeObj.scaleX || 1);
        activeObj._baseHeight = activeObj.height * (activeObj.scaleY || 1);
        activeObj._baseStrokeWidth = activeObj.strokeWidth || 2;
        activeObj.width = activeObj._baseWidth;
        activeObj.height = activeObj._baseHeight;
        activeObj.scaleX = 1;
        activeObj.scaleY = 1;
        activeObj.setCoords();
      }
      
      if (activeObj._originalSize === undefined) {
        const baseAvgSizePixels = (activeObj._baseWidth + activeObj._baseHeight) / 2;
        activeObj._originalSize = pixelsToSize(baseAvgSizePixels);
      }
      
      const originalSizePixels = sizeToPixels(activeObj._originalSize);
      const targetSizePixels = sizeToPixels(size);
      const scaleFactor = originalSizePixels > 0 ? targetSizePixels / originalSizePixels : 1;
      const newScaleX = scaleFactor;
      const newScaleY = scaleFactor;
      const newStrokeWidth = activeObj._baseStrokeWidth * scaleFactor;
      
      activeObj.set({
        fill: colorRgba,
        stroke: hexToRgba(color, Math.min(opacity + 0.3, 1.0)),
        strokeWidth: Math.max(1, newStrokeWidth),
        scaleX: newScaleX,
        scaleY: newScaleY,
        opacity: opacity
      });
      activeObj.setCoords();
      canvas.renderAll();
      snapshot();
      return;
    }
    
    if (activeObj.type === "ellipse") {
      if (!activeObj._baseRx) {
        activeObj._baseRx = activeObj.rx * (activeObj.scaleX || 1);
        activeObj._baseRy = activeObj.ry * (activeObj.scaleY || 1);
        activeObj._baseStrokeWidth = activeObj.strokeWidth || 2;
        activeObj.rx = activeObj._baseRx;
        activeObj.ry = activeObj._baseRy;
        activeObj.scaleX = 1;
        activeObj.scaleY = 1;
        activeObj.setCoords();
      }
      
      if (activeObj._originalSize === undefined) {
        const baseAvgSizePixels = (activeObj._baseRx + activeObj._baseRy) / 2;
        activeObj._originalSize = pixelsToSize(baseAvgSizePixels);
      }
      
      const originalSizePixels = sizeToPixels(activeObj._originalSize);
      const targetSizePixels = sizeToPixels(size);
      const scaleFactor = originalSizePixels > 0 ? targetSizePixels / originalSizePixels : 1;
      const newScaleX = scaleFactor;
      const newScaleY = scaleFactor;
      const newStrokeWidth = activeObj._baseStrokeWidth * scaleFactor;
      
      activeObj.set({
        fill: colorRgba,
        stroke: hexToRgba(color, Math.min(opacity + 0.3, 1.0)),
        strokeWidth: Math.max(1, newStrokeWidth),
        scaleX: newScaleX,
        scaleY: newScaleY,
        opacity: opacity
      });
      activeObj.setCoords();
      canvas.renderAll();
      snapshot();
      return;
    }
  }

  // Creates a new rectangle annotation at the pointer position
  function addRect(pointer) {
    const settings = getCurrentToolSettings();
    const fillColor = hexToRgba(settings.color, settings.opacity);
    const strokeColor = hexToRgba(settings.color, Math.min(settings.opacity + 0.3, 1.0));
    const sizeValue = settings.size || 30;
    const baseSizePixels = sizeToPixels(sizeValue);
    const width = baseSizePixels * 2;
    const height = baseSizePixels;
    const rect = new fabric.Rect({
      left: pointer.x, top: pointer.y, width: width, height: height,
      fill: fillColor, stroke: strokeColor, strokeWidth: Math.max(1, baseSizePixels / 30), rx: 2, ry: 2,
    });
    rect._baseWidth = width;
    rect._baseHeight = height;
    rect._baseStrokeWidth = rect.strokeWidth;
    rect._originalSize = sizeValue;
    afterPlace(rect);
  }

  let highlighterStart = null;
  let highlighterTemp = null;
  
  function startHighlighter(pointer) {
    const settings = getCurrentToolSettings();
    const fillColor = hexToRgba(settings.color, settings.opacity);
    highlighterStart = pointer;
    
    highlighterTemp = new fabric.Rect({
      left: pointer.x,
      top: pointer.y,
      width: 0,
      height: 0,
      fill: fillColor,
      stroke: "transparent",
      selectable: false,
      evented: false,
    });
    
    canvas.add(highlighterTemp);
    canvas.renderAll();
  }
  
  function updateHighlighter(pointer) {
    if (!highlighterStart || !highlighterTemp) return;
    
    const left = Math.min(highlighterStart.x, pointer.x);
    const top = Math.min(highlighterStart.y, pointer.y);
    const width = Math.abs(pointer.x - highlighterStart.x);
    const height = Math.abs(pointer.y - highlighterStart.y);
    
    highlighterTemp.set({
      left: left,
      top: top,
      width: width,
      height: height
    });
    canvas.renderAll();
  }
  
  function finishHighlighter() {
    if (!highlighterStart || !highlighterTemp) return;
    
    const settings = getCurrentToolSettings();
    const fillColor = hexToRgba(settings.color, settings.opacity);
    
    const width = highlighterTemp.width;
    const height = highlighterTemp.height;
    
    if (width > 1 && height > 1) {
      canvas.remove(highlighterTemp);
      
      const highlightRect = new fabric.Rect({
        left: highlighterTemp.left,
        top: highlighterTemp.top,
        width: width,
        height: height,
        fill: fillColor,
        stroke: "transparent",
        selectable: true,
        hasControls: true,
        evented: true,
      });
      
      afterPlace(highlightRect);
    } else {
      canvas.remove(highlighterTemp);
    }
    
    highlighterStart = null;
    highlighterTemp = null;
  }
  
  function setHighlighterPen() {
  }


  function setFreeDrawPen() {
    const settings = getCurrentToolSettings();
    canvas.freeDrawingBrush = new fabric.PencilBrush(canvas);
    canvas.freeDrawingBrush.color = hexToRgba(settings.color, settings.opacity);
    canvas.freeDrawingBrush.decimate = 1;
    canvas.freeDrawingBrush.width = settings.size;
  }

  // Creates a new ellipse annotation at the pointer position
  function addEllipse(pointer) {
    const settings = getCurrentToolSettings();
    const fillColor = hexToRgba(settings.color, settings.opacity);
    const strokeColor = hexToRgba(settings.color, Math.min(settings.opacity + 0.3, 1.0));
    const sizeValue = settings.size || 30;
    const baseSizePixels = sizeToPixels(sizeValue);
    const rx = baseSizePixels * 1.5;
    const ry = baseSizePixels;
    const ellipse = new fabric.Ellipse({
      left: pointer.x - rx, top: pointer.y - ry, rx: rx, ry: ry,
      originX: "left", originY: "top", fill: fillColor,
      stroke: strokeColor, strokeWidth: Math.max(1, baseSizePixels / 30),
    });
    ellipse._baseRx = rx;
    ellipse._baseRy = ry;
    ellipse._baseStrokeWidth = ellipse.strokeWidth;
    ellipse._originalSize = sizeValue;
    afterPlace(ellipse);
  }

  function addTextbox(pointer) {
    const tb = new fabric.Textbox("Text", {
      left: pointer.x,
      top: pointer.y,
      width: 240,
      fill: styles.textFill,
      fontSize: styles.textFontSize,
      fontFamily: "Inter, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial",
      editable: true,
    });
    afterPlace(tb);
    tb.enterEditing();
    tb.hiddenTextarea && tb.hiddenTextarea.focus();
  }

  let lineStart = null;
  let lineTemp = null;
  
  function startLine(pointer) {
    const settings = getCurrentToolSettings();
    const strokeColor = hexToRgba(settings.color, settings.opacity);
    lineStart = pointer;
    
    lineTemp = new fabric.Line(
      [pointer.x, pointer.y, pointer.x, pointer.y],
      {
        stroke: strokeColor,
        strokeWidth: settings.size,
        selectable: false,
        evented: false,
      }
    );
    
    canvas.add(lineTemp);
    canvas.renderAll();
  }
  
  function updateLine(pointer) {
    if (!lineStart || !lineTemp) return;
    lineTemp.set({ x2: pointer.x, y2: pointer.y });
    canvas.renderAll();
  }
  
  function finishLine() {
    if (!lineStart || !lineTemp) return;
    
    const settings = getCurrentToolSettings();
    const strokeColor = hexToRgba(settings.color, settings.opacity);
    
    const endX = lineTemp.x2;
    const endY = lineTemp.y2;
    
    canvas.remove(lineTemp);
    
    const line = new fabric.Line(
      [lineStart.x, lineStart.y, endX, endY],
      {
        stroke: strokeColor,
        strokeWidth: settings.size,
        selectable: true,
        hasControls: true,
        lockScalingFlip: true,
      }
    );
    
    afterPlace(line);
    
    lineStart = null;
    lineTemp = null;
  }
  
  function addLine(pointer) {
    startLine(pointer);
  }


  function addCheckbox(pointer) {
    const s = styles.checkboxSize;
    const box = new fabric.Rect({
      left: pointer.x, top: pointer.y, width: s, height: s,
      fill: "transparent", stroke: "#111", strokeWidth: 2, rx: 2, ry: 2, name: "box",
      selectable: false, evented: false,
    });
    const check = new fabric.Polyline(
      [
        { x: pointer.x + 3, y: pointer.y + s / 2 },
        { x: pointer.x + s / 3, y: pointer.y + s - 4 },
        { x: pointer.x + s - 3, y: pointer.y + 3 },
      ],
      { stroke: "#111", strokeWidth: 2, fill: null, name: "check", visible: false, selectable: false, evented: false }
    );
    const group = new fabric.Group([box, check], {
      selectable: true,
      hasControls: true,
      hasBorders: true,
      lockScalingFlip: true,
      lockRotation: true,
      hoverCursor: "pointer",
      data: { kind: "checkbox", checked: false },
    });
    afterPlace(group);
  }

  function eraseSelected() {
    const active = canvas.getActiveObjects();
    if (!active.length) return;
    editorState.suppressHistory = true;
    active.forEach((obj) => canvas.remove(obj));
    canvas.discardActiveObject();
    editorState.suppressHistory = false;
    canvas.requestRenderAll();
    snapshot();
  }

  function toggleCheckbox(checkboxGroup) {
    if (!checkboxGroup || checkboxGroup.type !== "group" || checkboxGroup.data?.kind !== "checkbox") {
      return false;
    }
    const chk = checkboxGroup._objects?.find(o => o.name === "check") || checkboxGroup._objects?.find(o => o.type === "polyline");
    if (!chk) return false;
    
    editorState.suppressHistory = true;
    chk.visible = !chk.visible;
    checkboxGroup.data = { ...(checkboxGroup.data || {}), kind: "checkbox", checked: chk.visible };
    checkboxGroup.dirty = true;
    editorState.suppressHistory = false;
    canvas.requestRenderAll();
    snapshot();
    return true;
  }


  let isPanning = false;
  let lastPos = null;
  let checkboxClickTimeout = null;
  let pendingCheckboxToggle = null;

  canvas.on("mouse:down", async (opt) => {
    await cancelInFlightRender();

    const evt = opt.e;
    const pointer = canvas.getPointer(evt);
    const interactingWithObject =
      !!opt.target || !!opt.transform || (opt.subTargets && opt.subTargets.length > 0);

    if (checkboxClickTimeout) {
      clearTimeout(checkboxClickTimeout);
      checkboxClickTimeout = null;
    }

    if (editorState.mode === "select" && opt.target && opt.target.type === "group" && opt.target.data?.kind === "checkbox") {
      const activeObj = canvas.getActiveObject();
      if (activeObj === opt.target) {
        setTimeout(() => {
          if (canvas.getActiveObject() === opt.target) {
            toggleCheckbox(opt.target);
          }
        }, 10);
      } else {
        pendingCheckboxToggle = opt.target;
        checkboxClickTimeout = setTimeout(() => {
          pendingCheckboxToggle = null;
          checkboxClickTimeout = null;
        }, 300);
      }
    } else {
      pendingCheckboxToggle = null;
    }

    if (editorState.mode === "pan") {
      isPanning = true;
      canvas.setCursor("grabbing");
      lastPos = canvas.getPointer(evt);
      evt.preventDefault();
      evt.stopPropagation();
      return;
    }

    if (editorState.mode === "eraser") {
      if (opt.target) {
        eraseSelected();
      }
      return;
    }

    if (interactingWithObject) return;

    if (editorState.mode === "rect")         addRect(pointer);
    else if (editorState.mode === "ellipse") addEllipse(pointer);
    else if (editorState.mode === "textbox") addTextbox(pointer);
    else if (editorState.mode === "pen-highlight") startHighlighter(pointer);
    else if (editorState.mode === "line")    addLine(pointer);
    else if (editorState.mode === "checkbox") addCheckbox(pointer);
  });

  canvas.on("mouse:move", (opt) => {
    if (isPanning) {
      const vpt = canvas.viewportTransform;
      const cur = canvas.getPointer(opt.e);
      vpt[4] += cur.x - lastPos.x;
      vpt[5] += cur.y - lastPos.y;
      lastPos = cur;
      canvas.requestRenderAll();
      return;
    }
    
    if (editorState.mode === "line" && lineStart) {
      const pointer = canvas.getPointer(opt.e);
      updateLine(pointer);
    } else if (editorState.mode === "pen-highlight" && highlighterStart) {
      const pointer = canvas.getPointer(opt.e);
      updateHighlighter(pointer);
    }
  });

  canvas.on("mouse:up", (opt) => {
    if (editorState.mode === "line" && lineStart) {
      finishLine();
      if (!stickyTools) setMode("select");
      return;
    } else if (editorState.mode === "pen-highlight" && highlighterStart) {
      finishHighlighter();
      if (!stickyTools) setMode("select");
      return;
    }

    isPanning = false;
    canvas.setCursor(editorState.mode === "pan" ? "grab" : "default");
  });

  canvas.on("path:created", (e) => {
    if (!stickyTools) setMode("select");
  });

  const keydownHandler = (e) => {
    const target = e.target;
    const isInputField = target.tagName === "INPUT" || 
                        target.tagName === "TEXTAREA" || 
                        target.isContentEditable;
    
    if (isInputField) {
      return;
    }

    if (isEditorToolbarLocked(editorState)) return;
    
    if (e.key === "Delete") {
      e.preventDefault();
      eraseSelected();
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
      e.preventDefault();
      e.shiftKey ? redo() : undo();
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
      e.preventDefault();
      redo();
    }
  };
  document.addEventListener("keydown", keydownHandler, true);

  btnPrev?.addEventListener("click", async () => {
    if (editorState.busy || editorState.rendering) return;
    await saveAnnotations(editorState.pageNum);
    
    if (editorState.pageNum <= 1) {
      editorState.pageNum = editorState.pageCount;
    } else {
      editorState.pageNum -= 1;
    }
    
    queueRenderPage(editorState.pageNum);
  });

  btnNext?.addEventListener("click", async () => {
    if (editorState.busy || editorState.rendering) return;
    await saveAnnotations(editorState.pageNum);
    
    if (editorState.pageNum >= editorState.pageCount) {
      editorState.pageNum = 1;
    } else {
      editorState.pageNum += 1;
    }
    
    queueRenderPage(editorState.pageNum);
  });

  pageNumInput?.addEventListener("change", async () => {
    if (editorState.busy || editorState.rendering) return;
    
    const targetPage = parseInt(pageNumInput.value, 10);
    
    if (!targetPage || targetPage < 1 || targetPage > editorState.pageCount) {
      alert(`Please enter a valid page number between 1 and ${editorState.pageCount}`);
      pageNumInput.value = String(editorState.pageNum);
      return;
    }
    
    if (targetPage === editorState.pageNum) return;
    
    await saveAnnotations(editorState.pageNum);
    editorState.pageNum = targetPage;
    queueRenderPage(editorState.pageNum);
  });

  // --- Zoom controls ---
  const zoomInBtn = document.getElementById("zoom-in");
  const zoomOutBtn = document.getElementById("zoom-out");
  const zoomResetBtn = document.getElementById("zoom-reset");
  const zoomLevelEl = document.getElementById("zoom-level");

  function updateZoomLabel() {
    if (zoomLevelEl) zoomLevelEl.textContent = Math.round(editorState.userZoom * 100) + "%";
  }

  function setElementsDisabled(elements, disabled, opacityWhenDisabled) {
    elements.forEach(function (el) {
      if (!el) return;
      el.disabled = disabled;
      if (disabled && opacityWhenDisabled != null) {
        el.style.opacity = String(opacityWhenDisabled);
        el.style.pointerEvents = "none";
      } else {
        el.style.opacity = "";
        el.style.pointerEvents = "";
      }
    });
  }

  function setCanvasInteractionBlocked(blocked) {
    if (blocked) {
      if (overlayCanvas) overlayCanvas.style.pointerEvents = "none";
      canvas.interactive = false;
      canvas.selection = false;
      return;
    }
    const mode = editorState.mode;
    if (mode === "edit-text") {
      canvas.interactive = false;
      canvas.selection = false;
      canvas.skipTargetFind = true;
      if (overlayCanvas) {
        overlayCanvas.style.pointerEvents = "none";
        overlayCanvas.style.zIndex = "1";
      }
      if (fabricContainerEl) fabricContainerEl.style.display = "none";
      return;
    }
    canvas.interactive = mode !== "pan";
    canvas.skipTargetFind = mode === "pan";
    canvas.selection = mode === "select";
    if (overlayCanvas) {
      overlayCanvas.style.pointerEvents = "auto";
      overlayCanvas.style.zIndex = "10";
    }
    if (fabricContainerEl) {
      fabricContainerEl.style.display = "";
      fabricContainerEl.style.pointerEvents = "auto";
    }
  }

  const EDITOR_TOOLBAR_CONTROLS = [
    btnSelect, btnPan, btnRect, btnPen, btnFreeDraw, btnEllipse, btnEditText,
    btnCheckbox, btnEraser, btnTextbox, btnLine, btnSignature, btnImageUpload,
    btnCancelTextEdits, btnSave, btnPrev, btnNext, btnUndo, btnRedo, btnFullscreen,
    pageNumInput, getCommentInput(),
    toolColorPicker, toolColorHex, toolOpacitySlider, toolSizeSlider,
    document.getElementById("tool-opacity-input"),
    document.getElementById("tool-size-input"),
    zoomInBtn, zoomOutBtn, zoomResetBtn,
    document.getElementById("text-format-bold"),
    document.getElementById("text-format-italic"),
    document.getElementById("text-format-underline"),
    document.getElementById("text-format-size"),
    document.getElementById("text-format-family"),
  ];

  const TEXT_EDIT_LOCKED_CONTROLS = [
    btnSelect, btnPan, btnRect, btnPen, btnFreeDraw, btnEllipse,
    btnCheckbox, btnEraser, btnTextbox, btnSignature, btnImageUpload,
    btnLine, btnSave, btnPrev, btnNext, btnUndo, btnRedo, pageNumInput,
  ];

  // Full lock during page render or save; text-edit subset otherwise.
  function syncEditorControls() {
    if (isEditorToolbarLocked(editorState)) {
      setElementsDisabled(EDITOR_TOOLBAR_CONTROLS, true, 0.5);
      setCanvasInteractionBlocked(true);
      return;
    }
    setElementsDisabled(EDITOR_TOOLBAR_CONTROLS, false);
    if (editorState.mode === "edit-text") {
      setElementsDisabled(TEXT_EDIT_LOCKED_CONTROLS, true, 0.4);
    }
    setCanvasInteractionBlocked(false);
    updateSaveButtonState();
  }

  function editorAdjustZoom(delta) {
    if (isEditorToolbarLocked(editorState)) return;
    editorState.userZoom = Math.min(Math.max(editorState.userZoom + delta, 0.25), 4.0);
    updateZoomLabel();
    queueRenderPage(editorState.pageNum);
  }

  zoomInBtn?.addEventListener("click", () => editorAdjustZoom(0.15));
  zoomOutBtn?.addEventListener("click", () => editorAdjustZoom(-0.15));
  zoomResetBtn?.addEventListener("click", () => {
    if (isEditorToolbarLocked(editorState)) return;
    editorState.userZoom = 1.0;
    updateZoomLabel();
    queueRenderPage(editorState.pageNum);
  });
  updateZoomLabel();

  // Ctrl+scroll zoom on the canvas area
  if (canvasWrapper) {
    canvasWrapper.addEventListener("wheel", (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        editorAdjustZoom(e.deltaY < 0 ? 0.1 : -0.1);
      }
    }, { passive: false });
  }

  pageNumInput?.addEventListener("blur", () => {
    const val = parseInt(pageNumInput.value, 10);
    if (!val || val < 1 || val > editorState.pageCount) {
      pageNumInput.value = String(editorState.pageNum);
    }
  });

  pageNumInput?.addEventListener("keypress", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      pageNumInput.blur();
    }
  });

  btnFullscreen?.addEventListener("click", () => {
    if (!document.fullscreenElement &&
        !document.mozFullScreenElement && !document.webkitFullscreenElement && !document.msFullscreenElement ) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      } else if (document.documentElement.msRequestFullscreen) {
        document.documentElement.msRequestFullscreen();
      } else if (document.documentElement.mozRequestFullScreen) {
        document.documentElement.mozRequestFullScreen();
      } else if (document.documentElement.webkitRequestFullscreen) {
        document.documentElement.webkitRequestFullscreen(Element.ALLOW_KEYBOARD_INPUT);
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.msExitFullscreen) {
        document.msExitFullscreen();
      } else if (document.mozCancelFullScreen) {
        document.mozCancelFullScreen();
      } else if (document.webkitExitFullscreen) {
        document.webkitExitFullscreen();
      }
    }
  });

  btnSelect?.addEventListener("click",  () => setMode("select"));
  btnPan?.addEventListener("click",     () => setMode("pan"));
  btnRect?.addEventListener("click",    () => { setMode("rect");    canvas.isDrawingMode = false; });
  btnPen?.addEventListener("click",     () => { setMode("pen-highlight"); canvas.isDrawingMode = false; });
  btnFreeDraw?.addEventListener("click", () => { setMode("free-draw"); });
  btnEllipse?.addEventListener("click", () => { setMode("ellipse"); canvas.isDrawingMode = false; });
  const editTextRadio = btnEditText?.querySelector('input[type="radio"]');
  // Only handle the click event directly to avoid double-triggering from radio change
  let editTextClickHandled = false;
  btnEditText?.addEventListener("click", (e) => {
    if (editTextClickHandled) {
      debugLog("[pdf_editor] Edit Text click ignored (already handled)");
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    editTextClickHandled = true;
    setTimeout(() => { editTextClickHandled = false; }, 300);
    
    e.stopPropagation();
    e.preventDefault();
    debugLog("[pdf_editor] Edit Text button clicked, current mode:", editorState.mode);
    if (editorState.mode === "edit-text") {
      debugLog("[pdf_editor] Exiting edit mode (Save Changes clicked)");
      const page = editorState.pageNum;
      const editor = getTextEditor();

      // Save any currently-active block edit into pending
      if (editor && editor.isEditing()) {
        const editResult = editor.saveEditing();
        if (editResult) {
          if (!editorState.pendingTextEdits[page]) editorState.pendingTextEdits[page] = {};
          editorState.pendingTextEdits[page][editResult.index] = editResult;
        }
      }

      // Flush ALL pending edits into editorState.textEdits
      const pending = editorState.pendingTextEdits[page] || {};
      const pendingKeys = Object.keys(pending);
      if (pendingKeys.length > 0) {
        // Require a comment before allowing the text edits to be saved.
        const commentEl = getCommentInput();
        const comment = normalizeComment(commentEl && commentEl.value);
        if (!comment) {
          if (typeof toastr !== "undefined") {
            toastr.warning("Please enter a comment before saving your changes.", "", { timeOut: 3000 });
          } else {
            alert("Please enter a comment before saving your changes.");
          }
          if (commentEl) commentEl.focus();
          return; // stay in edit-text mode; pending edits are preserved
        }

        if (!editorState.textEdits[page]) editorState.textEdits[page] = [];
        pendingKeys.forEach(idxStr => {
          const blockIndex = parseInt(idxStr, 10);
          const editResult = pending[idxStr];
          const existingIndex = editorState.textEdits[page].findIndex(edit => edit.index === blockIndex);
          if (existingIndex >= 0) {
            editorState.textEdits[page][existingIndex] = editResult;
          } else {
            editorState.textEdits[page].push(editResult);
          }
        });
        editorState.pendingTextEdits[page] = {};

        const flushedSet = new Set(pendingKeys.map(k => parseInt(k, 10)));
        renderTextLayer();

        if (editTextRadio) {
          editTextRadio.checked = false;
        }
        setMode("select");
        // Disable all UI until the save (and async rebuild/reload) finishes.
        // Cleared in reloadPdfAfterRebuild()'s finally, or on failure below.
        setSavingUI(true);
        if (commentEl) commentEl.value = "";
        saveTextEdits(page, true, true, flushedSet, comment).catch(err => {
          console.warn("[pdf_editor] Failed to save text edits to server:", err);
          setSavingUI(false);
        });
        return;
      } else {
        debugLog("[pdf_editor] No pending edits to save");
      }

      if (editTextRadio) {
        editTextRadio.checked = false;
      }
      setMode("select");
    } else {
      // setMode is the single gate for text editing — it checks
      // pageTextEditDisabled and shows the warning if blocked.
      if (editTextRadio) {
        editTextRadio.checked = true;
      }
      setMode("edit-text");
      // If setMode blocked entry, uncheck the radio
      if (editorState.mode !== "edit-text" && editTextRadio) {
        editTextRadio.checked = false;
      }
    }
  });
  
  
  btnCancelTextEdits?.addEventListener("click", () => {
    const editor = getTextEditor();
    if (editor && editor.isEditing()) {
      editor.cancelEditing();
    }
    // Discard all pending (unsaved) edits for this page
    const page = editorState.pageNum;
    if (editorState.pendingTextEdits[page] && Object.keys(editorState.pendingTextEdits[page]).length > 0) {
      debugLog(`[pdf_editor] Discarding ${Object.keys(editorState.pendingTextEdits[page]).length} pending edit(s) for page ${page}`);
      editorState.pendingTextEdits[page] = {};
    }
    setMode("select");
  });
  btnCheckbox?.addEventListener("click",() => { setMode("checkbox");canvas.isDrawingMode = false; });
  btnEraser?.addEventListener("click",  () => setMode("eraser"));
  btnUndo?.addEventListener("click", undo);
  btnRedo?.addEventListener("click", redo);

  btnTextbox?.addEventListener("click", () => { setMode("textbox"); canvas.isDrawingMode = false; });
  btnLine?.addEventListener("click", () => { setMode("line"); canvas.isDrawingMode = false; });

  if (toolColorPicker) {
    toolColorPicker.addEventListener("input", (e) => {
      const color = e.target.value;
      if (toolColorHex) toolColorHex.value = color;
      
      const activeObj = canvas.getActiveObject();
      if (activeObj && editorState.mode === "select") {
        const uiValues = getCurrentUIValues();
        updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
      } else {
        const settings = getCurrentToolSettings();
        updateCurrentToolSettings(color, settings.opacity, settings.size);
      }
    });
  }

  if (toolColorHex) {
    toolColorHex.addEventListener("input", (e) => {
      const color = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(color)) {
        if (toolColorPicker) toolColorPicker.value = color;
        
        const activeObj = canvas.getActiveObject();
        if (activeObj && editorState.mode === "select") {
          const uiValues = getCurrentUIValues();
          updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
        } else {
          const settings = getCurrentToolSettings();
          updateCurrentToolSettings(color, settings.opacity, settings.size);
        }
      }
    });
  }

  // Text Formatting Toolbar - Color Picker Synchronization (UI only, no functionality yet)
  const textFormatColor = document.getElementById("text-format-color");
  const textFormatColorHex = document.getElementById("text-format-color-hex");
  const textFormatBgColor = document.getElementById("text-format-bg-color");
  const textFormatBgColorHex = document.getElementById("text-format-bg-color-hex");
  const textFormatBgClear = document.getElementById("text-format-bg-clear");

  if (textFormatColor && textFormatColorHex) {
    textFormatColor.addEventListener("mousedown", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatColor.addEventListener("focus", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatColor.addEventListener("input", (e) => {
      const color = e.target.value;
      if (textFormatColorHex) textFormatColorHex.value = color;
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("foreColor", color);
      }
    });
  }

  if (textFormatColorHex) {
    textFormatColorHex.addEventListener("mousedown", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatColorHex.addEventListener("focus", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatColorHex.addEventListener("input", (e) => {
      const color = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(color)) {
        if (textFormatColor) textFormatColor.value = color;
        const editor = getTextEditor();
        if (editor && editor.isEditing()) {
          editor.applyFormatting("foreColor", color);
        }
      }
    });
  }

  if (textFormatBgColor && textFormatBgColorHex) {
    textFormatBgColor.addEventListener("mousedown", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatBgColor.addEventListener("focus", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatBgColor.addEventListener("input", (e) => {
      const color = e.target.value;
      if (textFormatBgColorHex) textFormatBgColorHex.value = color;
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("backColor", color);
      }
    });
  }

  if (textFormatBgColorHex) {
    textFormatBgColorHex.addEventListener("mousedown", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatBgColorHex.addEventListener("focus", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatBgColorHex.addEventListener("input", (e) => {
      const color = e.target.value;
      if (/^#[0-9A-F]{6}$/i.test(color)) {
        if (textFormatBgColor) textFormatBgColor.value = color;
        const editor = getTextEditor();
        if (editor && editor.isEditing()) {
          editor.applyFormatting("backColor", color);
        }
      }
    });
  }

  if (textFormatBgClear) {
    textFormatBgClear.addEventListener("click", () => {
      if (textFormatBgColor) textFormatBgColor.value = "#ffffff";
      if (textFormatBgColorHex) textFormatBgColorHex.value = "#ffffff";
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("backColor", "#ffffff");
      }
    });
  }

  // Text Formatting Toolbar - Format Buttons
  const textFormatBold = document.getElementById("text-format-bold");
  const textFormatItalic = document.getElementById("text-format-italic");
  const textFormatUnderline = document.getElementById("text-format-underline");

  if (textFormatBold) {
    textFormatBold.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("bold");
      }
    });
  }

  if (textFormatItalic) {
    textFormatItalic.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("italic");
      }
    });
  }

  if (textFormatUnderline) {
    textFormatUnderline.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("underline");
      }
    });
  }

  // Text Formatting Toolbar - Advanced Formatting Controls
  const textFormatSize = document.getElementById("text-format-size");
  const textFormatFamily = document.getElementById("text-format-family");
  const textFormatAlignLeft = document.getElementById("text-format-align-left");
  const textFormatAlignCenter = document.getElementById("text-format-align-center");
  const textFormatAlignRight = document.getElementById("text-format-align-right");
  const textFormatAlignJustify = document.getElementById("text-format-align-justify");

  if (textFormatSize) {
    // Save selection on mousedown (before focus, which collapses selection)
    textFormatSize.addEventListener("mousedown", (e) => {
      debugLog('[PDF Editor] Font size mousedown - saving selection');
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        const saved = editor.saveCurrentSelection();
        debugLog('[PDF Editor] Selection save result:', saved);
      }
    });

    // Don't save on focus - it happens after selection collapses
    // Don't save on click - it happens after focus
    // Only save on mousedown (before focus) and keydown for arrow keys

    textFormatSize.addEventListener("keydown", (e) => {
      debugLog('[PDF Editor] Font size keydown:', e.key);
      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        const editor = getTextEditor();
        if (editor && editor.isEditing()) {
          const saved = editor.saveCurrentSelection();
          debugLog('[PDF Editor] Selection save result (keydown):', saved);
        }
      }
    });
    // Clamp helper: never let the user pick a value above the dynamic neighbor-aware
    // cap (TextEditor#_refreshMaxFontSize updates input.max so this clamp tracks
    // the freshest computed maximum without us recomputing it here).
    const clampToCap = (rawSize, inputEl) => {
      const maxFontSizeFromDom = parseInt(inputEl.getAttribute("max"), 10);
      const effectiveMax = isNaN(maxFontSizeFromDom) ? 72 : Math.max(8, Math.min(72, maxFontSizeFromDom));
      const min = 8;
      let size = parseInt(rawSize, 10);
      if (isNaN(size)) return null;
      if (size < min) size = min;
      if (size > effectiveMax) size = effectiveMax;
      if (String(size) !== inputEl.value) inputEl.value = String(size);
      return size;
    };
    textFormatSize.addEventListener("input", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        const size = clampToCap(e.target.value, e.target);
        if (size != null) editor.applyFormatting("fontSize", size);
      }
    });
    textFormatSize.addEventListener("change", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        const size = clampToCap(e.target.value, e.target);
        if (size != null) editor.applyFormatting("fontSize", size);
      }
    });
  }

  if (textFormatFamily) {
    textFormatFamily.addEventListener("mousedown", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatFamily.addEventListener("focus", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.saveCurrentSelection();
      }
    });
    textFormatFamily.addEventListener("change", (e) => {
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("fontName", e.target.value);
      }
    });
  }

  if (textFormatAlignLeft) {
    textFormatAlignLeft.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("justifyLeft");
      }
    });
  }

  if (textFormatAlignCenter) {
    textFormatAlignCenter.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("justifyCenter");
      }
    });
  }

  if (textFormatAlignRight) {
    textFormatAlignRight.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("justifyRight");
      }
    });
  }

  if (textFormatAlignJustify) {
    textFormatAlignJustify.addEventListener("click", (e) => {
      e.preventDefault();
      const editor = getTextEditor();
      if (editor && editor.isEditing()) {
        editor.applyFormatting("justifyFull");
      }
    });
  }

  const toolOpacityInput = document.getElementById("tool-opacity-input");
  const toolSizeInput = document.getElementById("tool-size-input");

  if (toolOpacitySlider) {
    toolOpacitySlider.addEventListener("input", (e) => {
      const opacity = parseFloat(e.target.value);
      if (toolOpacityValue) toolOpacityValue.textContent = opacity.toFixed(1);
      if (toolOpacityInput) toolOpacityInput.value = opacity;
      
      const activeObj = canvas.getActiveObject();
      if (activeObj && editorState.mode === "select") {
        const uiValues = getCurrentUIValues();
        updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
      } else {
        const settings = getCurrentToolSettings();
        updateCurrentToolSettings(settings.color, opacity, settings.size);
      }
    });
  }

  if (toolOpacityInput) {
    toolOpacityInput.addEventListener("change", (e) => {
      const opacity = parseFloat(e.target.value);
      if (opacity >= 0 && opacity <= 1) {
        if (toolOpacitySlider) toolOpacitySlider.value = opacity;
        if (toolOpacityValue) toolOpacityValue.textContent = opacity.toFixed(1);
        
        const activeObj = canvas.getActiveObject();
        if (activeObj && editorState.mode === "select") {
          const uiValues = getCurrentUIValues();
          updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
        } else {
          const settings = getCurrentToolSettings();
          updateCurrentToolSettings(settings.color, opacity, settings.size);
        }
      }
    });
  }

  if (toolSizeSlider) {
    toolSizeSlider.addEventListener("input", (e) => {
      const size = parseInt(e.target.value);
      if (toolSizeValue) toolSizeValue.textContent = size;
      if (toolSizeInput) toolSizeInput.value = size;
      
      const activeObj = canvas.getActiveObject();
      if (activeObj && editorState.mode === "select") {
        const uiValues = getCurrentUIValues();
        updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
      } else {
        const settings = getCurrentToolSettings();
        updateCurrentToolSettings(settings.color, settings.opacity, size);
      }
    });
  }

  if (toolSizeInput) {
    toolSizeInput.addEventListener("change", (e) => {
      const size = parseInt(e.target.value);
      if (size >= 1 && size <= 100) {
        if (toolSizeSlider) toolSizeSlider.value = size;
        if (toolSizeValue) toolSizeValue.textContent = size;
        
        const activeObj = canvas.getActiveObject();
        if (activeObj && editorState.mode === "select") {
          const uiValues = getCurrentUIValues();
          updateCurrentToolSettings(uiValues.color, uiValues.opacity, uiValues.size);
        } else {
          const settings = getCurrentToolSettings();
          updateCurrentToolSettings(settings.color, settings.opacity, size);
        }
      }
    });
  }

  let currentSignatureMethod = "type";
  let signatureImageFile = null;

  function loadSavedSignatures() {
    if (!sigSavedList || !sigSavedSection) return;
    
    try {
      const keys = getSignatureStorageKeys();
      const savedPng = localStorage.getItem(keys.png);
      const savedData = localStorage.getItem(keys.data);
      
      if (savedPng && savedData) {
        const data = JSON.parse(savedData);
        sigSavedList.innerHTML = "";
        
        const card = document.createElement("div");
        card.style.cssText = "border:1px solid #dee2e6; border-radius:4px; padding:8px; background:#fff; cursor:pointer; flex:0 0 auto; max-width:150px;";
        card.title = "Click to use this saved signature";
        
        const preview = document.createElement("div");
        preview.style.cssText = "width:100%; height:60px; display:flex; align-items:center; justify-content:center; margin-bottom:4px; border:1px solid #eee; border-radius:2px; background:#fff;";
        
        if (data.type === "typed") {
          const escapedText = (data.text || "").replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
          preview.innerHTML = `<span style="font-family:${data.font}; font-size:20px; color:#333;">${escapedText}</span>`;
        } else {
          const img = document.createElement("img");
          img.src = savedPng;
          img.style.cssText = "max-width:100%; max-height:60px; object-fit:contain;";
          preview.appendChild(img);
        }
        
        const label = document.createElement("div");
        label.className = "small text-muted text-center";
        label.textContent = data.type === "typed" ? "Typed" : data.type === "drawn" ? "Drawn" : "Image";
        if (data.description) {
          label.textContent += `: ${data.description}`;
        }
        
        card.appendChild(preview);
        card.appendChild(label);
        
        card.addEventListener("click", () => {
          placeSignatureOnCanvas(savedPng, data);
        });
        
        sigSavedList.appendChild(card);
        sigSavedSection.style.display = "block";
        sigClearSaved.style.display = "block";
      } else {
        sigSavedSection.style.display = "none";
        sigClearSaved.style.display = "none";
      }
    } catch (e) {
      console.warn("Failed to load saved signatures", e);
      sigSavedSection.style.display = "none";
      sigClearSaved.style.display = "none";
    }
  }

  sigClearSaved?.addEventListener("click", () => {
    if (confirm("Are you sure you want to clear all saved signatures?")) {
      try {
        const keys = getSignatureStorageKeys();
        localStorage.removeItem(keys.png);
        localStorage.removeItem(keys.data);
        loadSavedSignatures();
      } catch (e) {
        console.warn("Failed to clear saved signatures", e);
      }
    }
  });

  function loadSignatureFonts() {
    const fonts = ["Dancing+Script", "Great+Vibes", "Allura", "Pacifico", "Satisfy", "Kalam"];
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = `https://fonts.googleapis.com/css2?${fonts.map(f => `family=${f}:wght@400&`).join("")}display=swap`;
    document.head.appendChild(link);
  }
  loadSignatureFonts();

  function switchSignatureTab(method) {
    currentSignatureMethod = method;
    
    [sigTabType, sigTabDraw, sigTabImage].forEach(tab => {
      if (tab) {
        tab.classList.remove("active");
        tab.setAttribute("aria-selected", "false");
      }
    });
    
    [sigPaneType, sigPaneDraw, sigPaneImage].forEach(pane => {
      if (pane) {
        pane.classList.remove("show", "active");
      }
    });
    
    if (method === "type" && sigTabType && sigPaneType) {
      sigTabType.classList.add("active");
      sigTabType.setAttribute("aria-selected", "true");
      sigPaneType.classList.add("show", "active");
    } else if (method === "draw" && sigTabDraw && sigPaneDraw) {
      sigTabDraw.classList.add("active");
      sigTabDraw.setAttribute("aria-selected", "true");
      sigPaneDraw.classList.add("show", "active");
    } else if (method === "image" && sigTabImage && sigPaneImage) {
      sigTabImage.classList.add("active");
      sigTabImage.setAttribute("aria-selected", "true");
      sigPaneImage.classList.add("show", "active");
    }
  }

  sigTabType?.addEventListener("click", () => switchSignatureTab("type"));
  sigTabDraw?.addEventListener("click", () => switchSignatureTab("draw"));
  sigTabImage?.addEventListener("click", () => switchSignatureTab("image"));

  function updateTypePreview() {
    if (!sigTypePreview || !sigTypeInput) return;
    const text = sigTypeInput.value.trim();
    const fontFamily = sigFontSelect?.value || "'Brush Script MT', cursive";
    
    if (text) {
      const escapedText = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');
      sigTypePreview.innerHTML = `<span style="font-family:${fontFamily}; font-size:32px; color:#333;">${escapedText}</span>`;
    } else {
      sigTypePreview.innerHTML = '<span class="text-muted small">Preview will appear here</span>';
    }
  }

  sigTypeInput?.addEventListener("input", updateTypePreview);
  sigFontSelect?.addEventListener("change", () => {
    if (sigTypeInput) {
      sigTypeInput.style.fontFamily = sigFontSelect.value;
    }
    updateTypePreview();
  });
  sigDescClear?.addEventListener("click", () => {
    if (sigDescInput) sigDescInput.value = "";
  });

  sigImageInput?.addEventListener("change", async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (sigImagePreview) {
      sigImagePreview.innerHTML = '<span class="text-muted small"><i class="fas fa-spinner fa-spin"></i> Processing image...</span>';
    }
    
    try {
      const dataUrl = await compressImage(file);
      signatureImageFile = dataUrl;
      
      if (sigImagePreview) {
        sigImagePreview.innerHTML = `<img src="${dataUrl}" style="max-width:100%; max-height:200px; border-radius:4px;" alt="Signature preview">`;
      }
    } catch (error) {
      console.error("Signature image processing error:", error);
      
      let errorMessage = error.message || 'An unknown error occurred while processing the image.';
      if (errorMessage.includes('too large')) {
        errorMessage += ' Please try a smaller image or compress it before uploading.';
      } else if (errorMessage.includes('corrupted')) {
        errorMessage += ' Please try a different image file.';
      } else if (errorMessage.includes('format')) {
        errorMessage += ' Supported formats: JPEG, PNG, GIF, WebP.';
      }
      
      alert(`Failed to process signature image: ${errorMessage}`);
      
      if (sigImagePreview) {
        sigImagePreview.innerHTML = '<span class="text-muted small">No image selected</span>';
      }
      if (sigImageInput) {
        sigImageInput.value = '';
      }
      signatureImageFile = null;
    }
  });

  function openSignaturePad() {
    if (!sigDialog) return;
    
    loadSavedSignatures();
    switchSignatureTab("type");
    
    if (sigTypeInput) sigTypeInput.value = "";
    if (sigDescInput) sigDescInput.value = "";
    if (sigImageInput) sigImageInput.value = "";
    if (sigImagePreview) sigImagePreview.innerHTML = '<span class="text-muted small">No image selected</span>';
    signatureImageFile = null;
    
    if (sigCtx && sigPad) {
      sigCtx.clearRect(0, 0, sigPad.width, sigPad.height);
    }
    
    updateTypePreview();
    
    if (typeof sigDialog.showModal === "function") sigDialog.showModal();
    else { sigDialog.setAttribute("open", "open"); sigDialog.style.display = "block"; }
  }

  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && sigDialog && sigDialog.hasAttribute("open")) {
      closeSignaturePad();
    }
  });
  
  function closeSignaturePad() {
    if (!sigDialog) return;
    if (sigDialog.close) sigDialog.close();
    sigDialog.removeAttribute("open");
    sigDialog.style.display = "";
  }

  let drawingSig = false; 
  let lastSigPt = null;
  function sigPointerPos(e){
    if (!sigPad) return {x:0, y:0};
    const r = sigPad.getBoundingClientRect();
    const scaleX = sigPad.width / r.width;
    const scaleY = sigPad.height / r.height;
    const t = e.touches?.[0] || e;
    return {
      x: (t.clientX - r.left) * scaleX,
      y: (t.clientY - r.top) * scaleY
    };
  }

  sigPad?.addEventListener("pointerdown", e => { 
    if (currentSignatureMethod !== "draw") return;
    drawingSig = true; 
    lastSigPt = sigPointerPos(e); 
    e.preventDefault(); 
  });
  
  sigPad?.addEventListener("pointermove", e => {
    if (!drawingSig || currentSignatureMethod !== "draw" || !sigCtx) return;
    const p = sigPointerPos(e);
    sigCtx.lineWidth = 2; 
    sigCtx.lineCap = "round";
    sigCtx.lineJoin = "round";
    sigCtx.strokeStyle = "#000000";
    sigCtx.beginPath(); 
    sigCtx.moveTo(lastSigPt.x, lastSigPt.y); 
    sigCtx.lineTo(p.x, p.y); 
    sigCtx.stroke();
    lastSigPt = p;
  });
  
  ["pointerup","pointerleave","pointercancel"].forEach(ev => 
    sigPad?.addEventListener(ev, () => drawingSig = false)
  );

  sigClear?.addEventListener("click", () => {
    if (sigCtx && sigPad) {
      sigCtx.clearRect(0, 0, sigPad.width, sigPad.height);
    }
  });
  
  sigCancel?.addEventListener("click", closeSignaturePad);

  sigSave?.addEventListener("click", async () => {
    let dataUrl = null;
    let signatureData = null;

    if (currentSignatureMethod === "type") {
      const text = sigTypeInput?.value.trim();
      if (!text) {
        alert("Please enter your signature text.");
        return;
      }
      
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = 400;
      canvas.height = 120;
      
      const fontFamily = sigFontSelect?.value || "'Brush Script MT', cursive";
      ctx.font = `32px ${fontFamily}`;
      ctx.fillStyle = "#000000";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      
      const metrics = ctx.measureText(text);
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
      
      dataUrl = canvas.toDataURL("image/png");
      signatureData = {
        type: "typed",
        text: text,
        font: fontFamily,
        description: sigDescInput?.value || ""
      };
      
    } else if (currentSignatureMethod === "draw") {
      if (!sigPad || !sigCtx) return;
      
      const imageData = sigCtx.getImageData(0, 0, sigPad.width, sigPad.height);
      const data = imageData.data;
      let hasContent = false;
      
      for (let i = 3; i < data.length; i += 16) {
        if (data[i] > 0) {
          hasContent = true;
          break;
        }
      }
      
      if (!hasContent) {
        alert("Please draw your signature.");
        return;
      }
      
      dataUrl = sigPad.toDataURL("image/png");
      signatureData = {
        type: "drawn",
        description: sigDescInput?.value || ""
      };
      
    } else if (currentSignatureMethod === "image") {
      if (!signatureImageFile) {
        alert("Please select an image file.");
        return;
      }
      dataUrl = signatureImageFile;
      signatureData = {
        type: "image",
        description: sigDescInput?.value || ""
      };
    }

    if (dataUrl) {
      placeSignatureOnCanvas(dataUrl, signatureData);
    }
  });

  function placeSignatureOnCanvas(dataUrl, signatureData) {
    closeSignaturePad();
    
    if (sigSaveCheckbox?.checked && signatureData) {
      try {
        const keys = getSignatureStorageKeys();
        localStorage.setItem(keys.png, dataUrl);
        localStorage.setItem(keys.data, JSON.stringify(signatureData));
      } catch {}
    }
    
    fabric.Image.fromURL(
      dataUrl,
      (img) => {
        img.set({ 
          left: 80, 
          top: 80, 
          selectable: true, 
          scaleX: 0.7, 
          scaleY: 0.7,
          data: signatureData || { type: "signature" }
        });
        editorState.suppressHistory = true;
        canvas.add(img).setActiveObject(img);
        editorState.suppressHistory = false;
        setMode("select");
        snapshot();
      },
      { crossOrigin: "anonymous" }
    );
  }

  btnSignature?.addEventListener("click", openSignaturePad);

  const IMAGE_CONFIG = {
    maxWidth: 2000,
    maxHeight: 2000,
    maxFileSize: 5 * 1024 * 1024, // 5MB in bytes
    quality: 0.85, // JPEG quality (0-1)
    allowedTypes: ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']
  };

  async function compressImage(file) {
    return new Promise((resolve, reject) => {
      if (!IMAGE_CONFIG.allowedTypes.includes(file.type)) {
        reject(new Error(`Unsupported image format. Please use: ${IMAGE_CONFIG.allowedTypes.join(', ')}`));
        return;
      }

      if (file.size > IMAGE_CONFIG.maxFileSize) {
        reject(new Error(`Image is too large. Maximum size is ${IMAGE_CONFIG.maxFileSize / (1024 * 1024)}MB`));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        
        const timeout = setTimeout(() => {
          reject(new Error('Image loading timed out. The file may be corrupted.'));
        }, 30000); // 30 second timeout

        img.onload = () => {
          clearTimeout(timeout);
          
          try {
            let { width, height } = img;
            const aspectRatio = width / height;

            if (width > 10000 || height > 10000) {
              reject(new Error('Image dimensions are too large. Maximum is 10000x10000 pixels.'));
              return;
            }

            if (width > IMAGE_CONFIG.maxWidth) {
              width = IMAGE_CONFIG.maxWidth;
              height = width / aspectRatio;
            }
            if (height > IMAGE_CONFIG.maxHeight) {
              height = IMAGE_CONFIG.maxHeight;
              width = height * aspectRatio;
            }

            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');

            ctx.drawImage(img, 0, 0, width, height);

            let dataUrl;
            try {
              if (file.type === 'image/png') {
                dataUrl = canvas.toDataURL('image/png');
              } else {
                dataUrl = canvas.toDataURL('image/jpeg', IMAGE_CONFIG.quality);
              }
            } catch (canvasError) {
              reject(new Error('Failed to process image. The file may be corrupted or unsupported.'));
              return;
            }

            const sizeInMB = (dataUrl.length * 3 / 4) / (1024 * 1024);
            if (sizeInMB > 10) {
              console.warn(`Compressed image is still large: ${sizeInMB.toFixed(2)}MB`);
            }

            img.onload = null;
            img.onerror = null;
            img.src = '';
            canvas.width = 0;
            canvas.height = 0;

            resolve(dataUrl);
          } catch (error) {
            clearTimeout(timeout);
            reject(new Error(`Failed to process image: ${error.message}`));
          }
        };
        
        img.onerror = () => {
          clearTimeout(timeout);
          reject(new Error('Failed to load image. The file may be corrupted or in an unsupported format.'));
        };
        
        img.src = e.target.result;
      };
      
      reader.onerror = () => reject(new Error('Failed to read file. Please try again.'));
      reader.onabort = () => reject(new Error('File reading was cancelled.'));
      
      reader.readAsDataURL(file);
    });
  }

  function placeImageOnCanvas(dataUrl, options = {}) {
    const {
      left = canvas.width / 2,
      top = canvas.height / 2,
      scale = 1.0,
      maxWidth = canvas.width * 0.8,
      maxHeight = canvas.height * 0.8
    } = options;

    fabric.Image.fromURL(
      dataUrl,
      (img) => {
        let scaleX = scale;
        let scaleY = scale;

        if (img.width * scaleX > maxWidth) {
          scaleX = maxWidth / img.width;
          scaleY = scaleX;
        }
        if (img.height * scaleY > maxHeight) {
          scaleY = maxHeight / img.height;
          scaleX = scaleY;
        }

        const finalLeft = left === canvas.width / 2 
          ? (canvas.width - img.width * scaleX) / 2 
          : left;
        const finalTop = top === canvas.height / 2 
          ? (canvas.height - img.height * scaleY) / 2 
          : top;

        img.set({
          left: finalLeft,
          top: finalTop,
          selectable: true,
          hasControls: true,
          hasBorders: true,
          scaleX: scaleX,
          scaleY: scaleY,
          stroke: null,
          strokeWidth: 0,
          data: { type: "image", uploadedAt: Date.now() }
        });

        editorState.suppressHistory = true;
        canvas.add(img).setActiveObject(img);
        editorState.suppressHistory = false;
        setMode("select");
        snapshot();
        
        requestAnimationFrame(() => {
          updateSettingsPanelForObject(img);
        });
      },
      { crossOrigin: "anonymous" }
    );
  }

  btnImageUpload?.addEventListener("click", () => {
    if (imageUploadInput) {
      imageUploadInput.click();
    }
  });

  imageUploadInput?.addEventListener("change", async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const originalText = btnImageUpload?.innerHTML;
    if (btnImageUpload) {
      btnImageUpload.disabled = true;
      btnImageUpload.innerHTML = '<i class="fas fa-spinner fa-spin pr-2"></i>Processing...';
    }

    try {
      const dataUrl = await compressImage(file);
      
      if (editorState.rendering) {
        throw new Error('Please wait for the page to finish loading before adding images.');
      }
      
      placeImageOnCanvas(dataUrl);
      
      if (btnImageUpload) {
        btnImageUpload.innerHTML = '<i class="fas fa-check pr-2"></i>Image Added';
        setTimeout(() => {
          if (btnImageUpload) {
            btnImageUpload.innerHTML = originalText;
            btnImageUpload.disabled = false;
          }
        }, 2000);
      }
    } catch (error) {
      console.error("Image upload error:", error);
      
      // Provide user-friendly error messages
      let errorMessage = error.message || 'An unknown error occurred while uploading the image.';
      if (errorMessage.includes('too large')) {
        errorMessage += ' Please try a smaller image or compress it before uploading.';
      } else if (errorMessage.includes('corrupted')) {
        errorMessage += ' Please try a different image file.';
      } else if (errorMessage.includes('format')) {
        errorMessage += ' Supported formats: JPEG, PNG, GIF, WebP.';
      }
      
      alert(`Failed to upload image: ${errorMessage}`);
      if (btnImageUpload) {
        btnImageUpload.innerHTML = originalText;
        btnImageUpload.disabled = false;
      }
    } finally {
      if (imageUploadInput) {
        imageUploadInput.value = '';
      }
    }
  });

  let srcPdfDocForSizes = null;
  const pageSizePtsCache = new Map();
  async function exportAnnotatedPdfForCurrentPage() {
    const { PDFDocument } = window.PDFLib;

    const active = canvas.getActiveObjects();
    const hadSelection = active && active.length > 0;

    const savedFlags = canvas.getObjects().map(o => ({
      o,
      hasBorders: o.hasBorders,
      hasControls: o.hasControls,
      objectCaching: o.objectCaching,
      borderColor: o.borderColor,
      cornerColor: o.cornerColor
    }));

    if (hadSelection) canvas.discardActiveObject();
    savedFlags.forEach(({ o }) => {
      o.hasBorders = false;
      o.hasControls = false;
      if (o.type === "image") {
        o.objectCaching = false;
        o.dirty = true;
        o.borderColor = "transparent";
        o.cornerColor = "transparent";
      }
    });
    canvas.requestRenderAll();
    await new Promise(r => requestAnimationFrame(r)); 

    // Get original PDF bytes for text embedding
    // Always fetch fresh to ensure we have the latest PDF after page operations
    let srcBytes;
    async function fetchPdf(url) {
      const r = await fetch(withCacheBuster(url), {
        credentials: "same-origin", headers: { "Cache-Control": "no-cache" },
      });
      if (!r.ok) {
        throw new Error(`Failed to fetch PDF: ${r.status} ${r.statusText}`);
      }
      return r.arrayBuffer();
    }
    try {
      srcBytes = await fetchPdf(editorState.pdfUrl);
    } catch (error) {
      if (error.message.includes("404") && editorState.currentPdfUrlPath) {
        try {
          const res = await fetch(editorState.currentPdfUrlPath, { credentials: "same-origin" });
          if (res.ok) {
            const data = await res.json();
            if (data.url) {
              editorState.pdfUrl = data.url;
              debugLog("[pdf_editor] PDF was updated, retrying with fresh URL");
              srcBytes = await fetchPdf(editorState.pdfUrl);
            } else {
              throw error;
            }
          } else {
            throw error;
          }
        } catch (retryError) {
          console.warn("[pdf_editor] Failed to fetch PDF for export (retry failed), reloading page...", retryError);
          const e = new Error("PDF was updated; reloading");
          e.reloadInsteadOfAlert = true;
          if (typeof toastr !== "undefined") {
            toastr.info("The PDF was updated. Reloading to show your changes...", "", { timeOut: 2000 });
          }
          setTimeout(() => window.location.reload(), 1000);
          throw e;
        }
      } else {
        console.warn("[pdf_editor] Failed to fetch PDF for export, reloading page...", error);
        const e = new Error("PDF fetch failed; reloading");
        e.reloadInsteadOfAlert = true;
        if (typeof toastr !== "undefined") {
          toastr.info("The PDF was updated. Reloading to show your changes...", "", { timeOut: 2000 });
        }
        setTimeout(() => window.location.reload(), 1000);
        throw e;
      }
    }

    // Always reload the PDF document to ensure we have the latest version
    // (page replacements/insertions/deletions change the PDF structure)
    srcPdfDocForSizes = await PDFDocument.load(srcBytes);
    
    // Clear page size cache for this page since PDF structure may have changed
    pageSizePtsCache.delete(editorState.pageNum);
    
    let size = pageSizePtsCache.get(editorState.pageNum);
    if (!size) {
      const srcPage = srcPdfDocForSizes.getPage(editorState.pageNum - 1);
      size = srcPage.getSize();
      pageSizePtsCache.set(editorState.pageNum, size);
    }

    // Embed text edits if any exist for this page
    let pdfWithText = new Uint8Array(srcBytes);
    if (window.PdfTextEmbedder && editorState.textEdits[editorState.pageNum] &&
        editorState.textEdits[editorState.pageNum].length > 0) {
      try {
        const viewport = editorState.pageTextContent?.viewport;
        const viewportInfo = viewport ? {
          width: viewport.width,
          height: viewport.height,
          scale: editorState.scale
        } : {};

        pdfWithText = await window.PdfTextEmbedder.embedTextIntoPdfPage(
          srcBytes,
          editorState.pageNum,
          editorState.textEdits[editorState.pageNum],
          viewportInfo
        );
      } catch (error) {
        console.warn("[pdf_editor] Failed to embed text edits into PDF:", error);
        // Continue with original PDF if embedding fails
      }
    }

    // Load the PDF (with or without embedded text) to extract the page
    const srcPdfDoc = await PDFDocument.load(pdfWithText);

    const out = await PDFDocument.create();
    const [copiedPage] = await out.copyPages(srcPdfDoc, [editorState.pageNum - 1]);
    const page = out.addPage(copiedPage);

    const doc = await ensurePdfDoc();
    const pdfPage = await doc.getPage(editorState.pageNum);
    const viewport = pdfPage.getViewport({ scale: editorState.scale });
    baseCanvas.width = viewport.width;
    baseCanvas.height = viewport.height;
    baseCtx.clearRect(0, 0, baseCanvas.width, baseCanvas.height);
    const task = pdfPage.render({ canvasContext: baseCtx, viewport });
    await task.promise;

    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = size.width;
    tmpCanvas.height = size.height;
    const tmpCtx = tmpCanvas.getContext("2d");
    tmpCtx.imageSmoothingEnabled = true;
    tmpCtx.imageSmoothingQuality = "high";
    tmpCtx.drawImage(baseCanvas, 0, 0, baseCanvas.width, baseCanvas.height, 0, 0, size.width, size.height);
    const overlayEl = canvas.toCanvasElement(1);
    tmpCtx.drawImage(overlayEl, 0, 0, overlayEl.width, overlayEl.height, 0, 0, size.width, size.height);
    const dataUrl = tmpCanvas.toDataURL("image/png");
    const pngBytes = await fetch(dataUrl).then(r => r.arrayBuffer());
    const png = await out.embedPng(pngBytes);
    page.drawImage(png, { x: 0, y: 0, width: size.width, height: size.height });
    page.setMediaBox?.(0,0,size.width,size.height);
    page.setCropBox?.(0,0,size.width,size.height);
    page.setBleedBox?.(0,0,size.width,size.height);
    page.setTrimBox?.(0,0,size.width,size.height);
    page.setArtBox?.(0,0,size.width,size.height);

    const fullPageBytes = await out.save();

    // Build annotation-only overlay PDF (transparent background, annotations only)
    const overlayOnlyCanvas = document.createElement("canvas");
    overlayOnlyCanvas.width = size.width;
    overlayOnlyCanvas.height = size.height;
    const overlayOnlyCtx = overlayOnlyCanvas.getContext("2d");
    overlayOnlyCtx.imageSmoothingEnabled = true;
    overlayOnlyCtx.imageSmoothingQuality = "high";
    overlayOnlyCtx.drawImage(overlayEl, 0, 0, overlayEl.width, overlayEl.height, 0, 0, size.width, size.height);
    const overlayDataUrl = overlayOnlyCanvas.toDataURL("image/png");
    const overlayPngBytes = await fetch(overlayDataUrl).then(r => r.arrayBuffer());
    const overlayPdfDoc = await PDFDocument.create();
    const overlayPdfPage = overlayPdfDoc.addPage([size.width, size.height]);
    const overlayPng = await overlayPdfDoc.embedPng(overlayPngBytes);
    overlayPdfPage.drawImage(overlayPng, { x: 0, y: 0, width: size.width, height: size.height });
    const overlayPdfBytes = await overlayPdfDoc.save();

    savedFlags.forEach(({ o, hasBorders, hasControls, objectCaching, borderColor, cornerColor }) => {
      o.hasBorders = hasBorders;
      o.hasControls = hasControls;
      if (objectCaching !== undefined) o.objectCaching = objectCaching;
      if (borderColor !== undefined) o.borderColor = borderColor;
      if (cornerColor !== undefined) o.cornerColor = cornerColor;
    });
    if (hadSelection && active.length) {
      try {
        canvas.setActiveObject(
          active.length === 1 ? active[0] : new fabric.ActiveSelection(active, { canvas })
        );
      } catch {}
    }
    canvas.requestRenderAll();

    return {
      fullPage: new Blob([fullPageBytes], { type: "application/pdf" }),
      overlay: new Blob([overlayPdfBytes], { type: "application/pdf" })
    };
  }

  async function uploadPreviewPage(fullPageBlob, overlayBlob, page, previewUrl, comment) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const fd = new FormData();
    fd.append("page", String(page));
    fd.append("file", fullPageBlob, "preview-page.pdf");
    if (overlayBlob) fd.append("annotation_overlay", overlayBlob, "annotation-overlay.pdf");
    if (comment) fd.append("comments", comment);
    if (token) fd.append("authenticity_token", token);

    const progressBar = document.getElementById("preview-upload-progress");
    
    if (previewProgressResetTimeout) {
      clearTimeout(previewProgressResetTimeout);
      previewProgressResetTimeout = null;
    }
    
    if (progressBar) {
      progressBar.style.width = "0%";
      progressBar.setAttribute("aria-valuenow", "0");
    }

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener("progress", (evt) => {
        if (evt.lengthComputable && progressBar) {
          const percentComplete = Math.floor((evt.loaded / evt.total) * 100);
          progressBar.style.width = percentComplete + "%";
          progressBar.setAttribute("aria-valuenow", percentComplete);
        }
      }, false);
      
      xhr.addEventListener("load", () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          if (progressBar) {
            progressBar.style.width = "100%";
            progressBar.setAttribute("aria-valuenow", "100");
          }
          previewProgressResetTimeout = setTimeout(() => {
            if (progressBar) {
              progressBar.style.width = "0%";
              progressBar.setAttribute("aria-valuenow", "0");
            }
            previewProgressResetTimeout = null;
          }, 2000);
          resolve(xhr.response);
        } else {
          if (progressBar) {
            progressBar.style.width = "0%";
            progressBar.setAttribute("aria-valuenow", "0");
          }
          reject(new Error(`Preview upload failed: ${xhr.status}`));
        }
      });
      
      xhr.addEventListener("error", () => {
        if (progressBar) {
          progressBar.style.width = "0%";
          progressBar.setAttribute("aria-valuenow", "0");
        }
        reject(new Error("Preview upload failed: network error"));
      });
      
      xhr.open("POST", previewUrl);
      if (token) {
        xhr.setRequestHeader("X-CSRF-Token", token);
      }
      xhr.send(fd);
    });
  }

  btnSave?.addEventListener("click", async () => {
    if (editorState.rendering) return;

    // Check if annotation state is actually dirty before saving
    if (!editorState.hasUnsavedChanges) {
      if (typeof toastr !== "undefined") {
        toastr.info("No annotation changes to save.", "", { timeOut: 2000 });
      }
      debugLog("[pdf_editor] Save Current Page skipped — no annotation changes");
      return;
    }

    // Require a comment before allowing annotations to be saved.
    const commentEl = getCommentInput();
    const comment = normalizeComment(commentEl && commentEl.value);
    if (!comment) {
      if (typeof toastr !== "undefined") {
        toastr.warning("Please enter a comment before saving this page.", "", { timeOut: 3000 });
      } else {
        alert("Please enter a comment before saving this page.");
      }
      if (commentEl) commentEl.focus();
      return;
    }

    try {
      // Disable all UI for the duration of the save (annotation saves do not reload the page).
      setSavingUI(true);

      // Fetch fresh PDF URL before save to avoid 404 from stale blob (rebuilt preview purges old blobs)
      if (editorState.currentPdfUrlPath) {
        try {
          const res = await fetch(editorState.currentPdfUrlPath, { credentials: "same-origin" });
          if (res.ok) {
            const data = await res.json();
            if (data.url) editorState.pdfUrl = data.url;
          }
        } catch (_) {}
      }

      await saveAnnotations(editorState.pageNum, true);

      // Upload annotated page to create amendment record, then server rebuilds with all amendments + text edits
      if (editorState.previewUrl) {
        flushLocalDraft(editorState.pageNum);
        const result = await exportAnnotatedPdfForCurrentPage();
        await uploadPreviewPage(result.fullPage, result.overlay, editorState.pageNum, editorState.previewUrl, comment);
        commitPublishedBaselineAfterSave(editorState.pageNum);
      }

      if (commentEl) commentEl.value = "";

      if (typeof toastr !== "undefined") {
        toastr.success("Annotations saved. Preview updated.", "", { timeOut: 2000 });
      }
    } catch (err) {
      if (err.reloadInsteadOfAlert) {
        debugLog("[pdf_editor] PDF was updated, reload scheduled");
        return;
      }
      console.error("[pdf_editor] save failed", err);
      alert("Failed to save or update preview. See console.");
    } finally {
      setSavingUI(false);
      updateSaveButtonState();
    }
  });

  try {
    await ensurePdfDoc({ force: true });
    editorState.pageCount = editorState.pdfDoc.numPages;
    if (pageCountEl) pageCountEl.textContent = String(editorState.pageCount);
    editorState.pageNum = 1;
    // Restore page number after text-edit save reload
    try {
      const restorePage = sessionStorage.getItem('pdf_editor_restore_page');
      if (restorePage) {
        sessionStorage.removeItem('pdf_editor_restore_page');
        const rp = parseInt(restorePage, 10);
        if (rp > 0 && rp <= editorState.pageCount) {
          editorState.pageNum = rp;
          if (DEBUG_TEXT_BLOCKS) debugLog("[pdf_editor] Restored page after save:", rp);
        }
      }
    } catch(_) {}
    setMode("select");
    editorState.rendering = true;
    syncEditorControls();
    requestAnimationFrame(() => renderPage(editorState.pageNum));
  } catch (e) {
    console.error("[pdf_editor] failed to load PDF", e);
  }

  function teardownPdfEditor() {
    try { document.removeEventListener("keydown", keydownHandler, true); } catch {}
    try { sigDialog?.close?.(); sigDialog?.removeAttribute?.("open"); } catch {}
    try { editorState.renderTask?.cancel(); } catch {}
    try { editorState._loadingTask?.destroy?.(); } catch {}
    try { editorState.pdfDoc?.destroy?.(); } catch {}

    try { canvas?.dispose(); } catch {}
    try { baseCtx?.clearRect(0, 0, baseCanvas.width, baseCanvas.height); } catch {}
    try { overlayCanvas.width = overlayCanvas.height = 0;
          baseCanvas.width    = baseCanvas.height    = 0; } catch {}
    container.dataset.initialized = "false";
  }

  function baseAnnotationsKey() {
    return annotationsStorageBase();
  }
  function lastKnownKey(page) {
    return `${baseAnnotationsKey()}::last::page:${page}`;
  }
  function findAnyRevLocalKey(page) {
    const base = baseAnnotationsKey();
    const suffix = `::page:${page}`;
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(base + "::rev:") && k.endsWith(suffix)) return k;
    }
    return null;
  }

  window.addEventListener("beforeunload", (e) => {
    const hasPendingEdits = Object.keys(editorState.pendingTextEdits || {}).some(
      pg => Object.keys(editorState.pendingTextEdits[pg] || {}).length > 0
    );
    if (editorState.hasUnsavedChanges || hasPendingEdits) {
      e.preventDefault();
      e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
      return e.returnValue;
    }
  });

  document.addEventListener("turbolinks:before-cache", async () => {
    try { 
      if (editorState.hasUnsavedChanges) {
        await saveAnnotations(editorState.pageNum);
      }
    } catch (e) {
      console.warn("[pdf_editor] Failed to autosave before cache", e);
    }
    teardownPdfEditor();
  }, { once: true });
  document.addEventListener("turbolinks:before-render", async () => {
    try { 
      if (editorState.hasUnsavedChanges) {
        await saveAnnotations(editorState.pageNum);
      }
    } catch (e) {
      console.warn("[pdf_editor] Failed to autosave before render", e);
    }
    teardownPdfEditor();
  }, { once: true });

  window.addEventListener("pagehide", () => {
    try { teardownPdfEditor(); } catch {}
  }, { once: true });
}

document.addEventListener("turbolinks:load", () => { initPdfEditor().catch(console.error); });
